import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box, Typography, Paper, Button, TextField, Autocomplete, CircularProgress,
  Alert, Chip, Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  Collapse, LinearProgress, Divider, Stack, Card, CardContent, Tabs, Tab,
  FormControlLabel, Switch, Slider, ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import WarningIcon from '@mui/icons-material/Warning';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import HistoryIcon from '@mui/icons-material/History';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckIcon from '@mui/icons-material/Check';
import RefreshIcon from '@mui/icons-material/Refresh';
import CropFreeIcon from '@mui/icons-material/CropFree';
import {
  searchAssets, extractAndCompare, extractAndCompareV2, getHistory, getComparison, resolveFields,
  getDocumentUrl, getExportUrl, deleteComparison, deleteComparisons,
  ComparisonResult, ComparisonField, ComparisonHistoryItem, ComparisonDetail,
  AssetSearchResult, AiProvider, HistoryMode, LabelPdfCropSelection, V2ComparisonResultData, V2ComparisonRow, V2RowStatus,
} from '../../services/productComparisonService';
import axiosInstance from '../../services/axiosInstance';
import LabelPdfCropDialog from './LabelPdfCropDialog';
import PdfPreviewSurface from './PdfPreviewSurface';

const CONFIDENCE_COLOR: Record<string, string> = {
  high: '#2e7d32',
  medium: '#ed6c02',
  low: '#d32f2f',
};

function ConfidenceDot({ level }: { level: string | null }) {
  if (!level) return null;
  return (
    <Tooltip title={`Konfidens: ${level === 'high' ? 'Hög' : level === 'medium' ? 'Medel' : 'Låg'}`}>
      <Box
        component="span"
        sx={{
          display: 'inline-block',
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: CONFIDENCE_COLOR[level] || '#999',
          ml: 0.5,
          verticalAlign: 'middle',
        }}
      />
    </Tooltip>
  );
}

function formatAiProvider(provider: string | null | undefined, model?: string | null): string {
  if (provider === 'openai') {
    return model === 'gpt-5.4' ? 'GPT 5.4' : `GPT (${model || 'OpenAI'})`;
  }
  return 'Claude';
}

function formatOverallStatus(status: string): string {
  if (status === 'ok') return 'OK';
  if (status === 'has_minor_differences') return 'Mindre skillnader';
  if (status === 'has_regulatory_warnings') return 'Regulatoriska varningar';
  if (status === 'has_mismatches') return 'Avvikelser';
  return status;
}

function getComparisonVersion(value: { comparisonVersion?: string | null } | null | undefined): 'v1' | 'v2' {
  return value?.comparisonVersion === 'v2' ? 'v2' : 'v1';
}

function isV2Comparison(
  value: { comparisonVersion?: string | null; v2Result?: V2ComparisonResultData | null } | null | undefined,
): value is { comparisonVersion: 'v2'; v2Result: V2ComparisonResultData } {
  return value?.comparisonVersion === 'v2' && !!value.v2Result;
}

const PC_COLORS = {
  bg: 'var(--pc-bg)',
  surface: 'var(--pc-surface)',
  surface2: 'var(--pc-surface-2)',
  border: 'var(--pc-border)',
  borderStrong: 'var(--pc-border-strong)',
  textPrimary: 'var(--pc-text-primary)',
  textSecondary: 'var(--pc-text-secondary)',
  textMuted: 'var(--pc-text-muted)',
  accent: 'var(--pc-accent)',
  ok: 'var(--pc-ok)',
  okBg: 'var(--pc-ok-bg)',
  okBorder: 'var(--pc-ok-border)',
  warn: 'var(--pc-warn)',
  warnBg: 'var(--pc-warn-bg)',
  warnBorder: 'var(--pc-warn-border)',
  crit: 'var(--pc-crit)',
  critBg: 'var(--pc-crit-bg)',
  critBorder: 'var(--pc-crit-border)',
  solo: 'var(--pc-solo)',
  soloBg: 'var(--pc-solo-bg)',
  soloBorder: 'var(--pc-solo-border)',
  dam: 'var(--pc-dam)',
  pdf: 'var(--pc-pdf)',
  sup: 'var(--pc-sup)',
} as const;

const STICKY_HEADER_SX = {
  position: 'sticky',
  top: 0,
  zIndex: 20,
  mb: 2.5,
  pb: 1.5,
  bgcolor: PC_COLORS.bg,
  borderBottom: '1px solid',
  borderColor: PC_COLORS.border,
  boxShadow: '0 8px 24px rgba(26, 25, 22, 0.06)',
} as const;

// Legacy V1 is intentionally hidden in the UI because the team has decided
// to use V2 going forward. Keep the underlying code temporarily until V1 can
// be removed in a separate cleanup.
const SHOW_LEGACY_V1_ACTIONS = false;

const SOURCE_META = {
  dam: {
    label: 'DAM / Dabas',
    shortLabel: 'DAM / DABAS',
    icon: '📁',
    accent: PC_COLORS.dam,
    softBg: '#EEF2FF',
    softText: '#4F46E5',
  },
  pdf: {
    label: 'Etikett (PDF)',
    shortLabel: 'ETIKETT (PDF)',
    icon: '📄',
    accent: PC_COLORS.pdf,
    softBg: '#ECFDF5',
    softText: '#059669',
  },
  word: {
    label: 'Leverantör',
    shortLabel: 'LEVERANTÖR',
    icon: '📝',
    accent: PC_COLORS.sup,
    softBg: '#FFFBEB',
    softText: '#D97706',
  },
} as const;

const FIELD_ICON_BY_KEY: Record<string, string> = {
  naringsvarden_per_100g: '📊',
  innehallsdeklaration: '🌿',
  allergener: '⚠️',
  nettovikt: '⚖️',
  avrunnen_vikt: '💧',
  produktnamn: '🏷️',
  forvaring: '❄️',
};

function getFieldIcon(fieldKey: string): string {
  return FIELD_ICON_BY_KEY[fieldKey] || '•';
}

function getStatusAppearance(status: ComparisonField['status'], resolved = false) {
  if (resolved) {
    return { label: 'Hanterad', symbol: '✓', color: PC_COLORS.textSecondary, bg: PC_COLORS.surface2, border: PC_COLORS.borderStrong };
  }
  switch (status) {
    case 'match':
      return { label: 'Överensstämmer', symbol: '✓', color: PC_COLORS.ok, bg: PC_COLORS.okBg, border: PC_COLORS.okBorder };
    case 'mismatch':
      return { label: 'Avvikelse', symbol: '⚠', color: PC_COLORS.crit, bg: PC_COLORS.critBg, border: PC_COLORS.critBorder };
    case 'only_one_source':
      return { label: 'Bara en källa', symbol: '○', color: PC_COLORS.solo, bg: PC_COLORS.soloBg, border: PC_COLORS.soloBorder };
    default:
      return { label: 'Saknas', symbol: '–', color: PC_COLORS.textMuted, bg: PC_COLORS.bg, border: PC_COLORS.border };
  }
}

function StatusBadge({
  status,
  resolved = false,
}: {
  status: ComparisonField['status'];
  resolved?: boolean;
}) {
  const appearance = getStatusAppearance(status, resolved);
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1.25,
        py: 0.5,
        borderRadius: '999px',
        border: '1px solid',
        borderColor: appearance.border,
        bgcolor: appearance.bg,
        color: appearance.color,
        fontSize: '0.72rem',
        fontWeight: 600,
        letterSpacing: '0.02em',
        whiteSpace: 'nowrap',
      }}
    >
      <Box component="span" sx={{ fontSize: '0.78rem', lineHeight: 1 }}>{appearance.symbol}</Box>
      <Box component="span">{appearance.label}</Box>
    </Box>
  );
}

function OverallStatusBadge({ status }: { status: string }) {
  if (status === 'has_minor_differences') {
    return (
      <Chip
        label="Mindre skillnader"
        size="small"
        variant="outlined"
        sx={{
          borderColor: '#F9C06A',
          bgcolor: '#FEF3E2',
          color: '#92400E',
          fontWeight: 600,
        }}
      />
    );
  }

  return <StatusBadge status={status === 'ok' ? 'match' : 'mismatch'} />;
}

function ComparisonVersionBadge({ version }: { version: 'v1' | 'v2' }) {
  return (
    <Chip
      size="small"
      label={version.toUpperCase()}
      variant="outlined"
      sx={{
        borderColor: version === 'v2' ? '#BFDBFE' : PC_COLORS.borderStrong,
        bgcolor: version === 'v2' ? '#EFF6FF' : PC_COLORS.surface,
        color: version === 'v2' ? '#1E3A5F' : PC_COLORS.textPrimary,
        fontWeight: 700,
      }}
    />
  );
}

function SummaryStatCard({
  value,
  label,
  subtitle,
  tone,
}: {
  value: number;
  label: string;
  subtitle: string;
  tone: 'ok' | 'warn' | 'miss' | 'solo';
}) {
  const color = tone === 'ok'
    ? PC_COLORS.ok
    : tone === 'warn'
      ? '#92400E'
      : tone === 'solo'
        ? PC_COLORS.solo
        : PC_COLORS.textMuted;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.75,
        px: { xs: 0, sm: 2.5 },
        py: { xs: 1.25, sm: 0 },
        borderRight: { xs: 'none', sm: '1px solid' },
        borderBottom: { xs: '1px solid', sm: 'none' },
        borderColor: PC_COLORS.border,
        '&:last-of-type': { borderRight: 'none', borderBottom: 'none' },
      }}
    >
      <Typography
        className="pc-mono"
        sx={{
          fontSize: '2rem',
          lineHeight: 1,
          fontWeight: 600,
          color,
        }}
      >
        {value}
      </Typography>
      <Box>
        <Typography sx={{ fontSize: '0.84rem', fontWeight: 600, color }}>{label}</Typography>
        <Typography sx={{ fontSize: '0.74rem', color: PC_COLORS.textSecondary }}>{subtitle}</Typography>
      </Box>
    </Box>
  );
}

function ComparisonScoreBar({ fields }: { fields: ComparisonField[] }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 2, sm: '16px 24px' },
        mb: 2.5,
        borderRadius: '10px',
        borderColor: PC_COLORS.border,
        bgcolor: PC_COLORS.surface,
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' },
        }}
      >
        <SummaryStatCard
          value={fields.filter(f => f.status === 'match').length}
          label="Överensstämmer"
          subtitle="Alla källor matchar"
          tone="ok"
        />
        <SummaryStatCard
          value={fields.filter(f => f.status === 'mismatch').length}
          label="Avvikelser"
          subtitle="Kräver granskning"
          tone="warn"
        />
        <SummaryStatCard
          value={fields.filter(f => f.status === 'missing').length}
          label="Saknas"
          subtitle="Data ej tillgänglig"
          tone="miss"
        />
        <SummaryStatCard
          value={fields.filter(f => f.status === 'only_one_source').length}
          label="Bara en källa"
          subtitle="Ej verifierbar"
          tone="solo"
        />
      </Box>
    </Paper>
  );
}

function CriticalWarningBanner({ warnings }: { warnings: string[] }) {
  if (warnings.length === 0) return null;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        px: 2.25,
        py: 1.75,
        mb: 2.5,
        borderRadius: '8px',
        border: '1px solid',
        borderColor: PC_COLORS.critBorder,
        borderLeft: '4px solid',
        borderLeftColor: PC_COLORS.crit,
        bgcolor: PC_COLORS.critBg,
        animation: 'pcSlideIn 0.3s ease',
      }}
    >
      <Box
        sx={{
          width: 20,
          height: 20,
          borderRadius: '50%',
          bgcolor: PC_COLORS.crit,
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.72rem',
          fontWeight: 700,
          flexShrink: 0,
          mt: '2px',
        }}
      >
        !
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: PC_COLORS.crit, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 0.25 }}>
          Regulatorisk varning
        </Typography>
        <Typography sx={{ fontSize: '0.84rem', color: '#7F1D1D' }}>
          <Box
            component="span"
            sx={{
              display: 'inline-block',
              mr: 0.75,
              px: 0.9,
              py: 0.15,
              borderRadius: '4px',
              bgcolor: PC_COLORS.crit,
              color: '#fff',
              fontSize: '0.63rem',
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              verticalAlign: 'middle',
            }}
          >
            Kritisk
          </Box>
          {warnings[0]}
        </Typography>
      </Box>
    </Box>
  );
}

function ComparisonSurface({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 2.25, sm: 3 },
        borderRadius: '12px',
        borderColor: PC_COLORS.border,
        bgcolor: PC_COLORS.surface,
        boxShadow: '0 1px 0 rgba(26, 25, 22, 0.02)',
      }}
    >
      {children}
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Drop zone component
// ---------------------------------------------------------------------------
function DropZone({
  label,
  accept,
  file,
  onFile,
  icon,
}: {
  label: string;
  accept: string;
  file: File | null;
  onFile: (f: File | null) => void;
  icon: React.ReactNode;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) onFile(f);
  }, [onFile]);

  return (
    <Paper
      variant="outlined"
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      onClick={() => inputRef.current?.click()}
      sx={{
        p: 3,
        textAlign: 'center',
        cursor: 'pointer',
        borderStyle: 'dashed',
        borderWidth: 1.5,
        borderColor: dragOver ? PC_COLORS.accent : file ? PC_COLORS.okBorder : PC_COLORS.borderStrong,
        backgroundColor: dragOver ? '#F5F8FF' : file ? PC_COLORS.okBg : PC_COLORS.surface2,
        transition: 'all 0.2s',
        '&:hover': { borderColor: PC_COLORS.accent, backgroundColor: '#F5F8FF' },
        minHeight: 148,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1.25,
        borderRadius: '10px',
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        hidden
        onChange={(e) => onFile(e.target.files?.[0] || null)}
      />
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: '10px',
          border: '1px solid',
          borderColor: file ? PC_COLORS.okBorder : PC_COLORS.border,
          bgcolor: PC_COLORS.surface,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {icon}
      </Box>
      <Typography sx={{ fontSize: '0.84rem', color: PC_COLORS.textSecondary, maxWidth: 280 }}>
        {file ? file.name : label}
      </Typography>
      {file && (
        <Chip
          label={`${(file.size / 1024).toFixed(0)} KB`}
          size="small"
          variant="outlined"
          onDelete={() => onFile(null)}
          sx={{
            bgcolor: PC_COLORS.surface,
            borderColor: PC_COLORS.okBorder,
            color: PC_COLORS.ok,
            '& .MuiChip-deleteIcon': { color: PC_COLORS.ok },
          }}
        />
      )}
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Multi-file drop zone (for multiple PDFs: front+back)
// ---------------------------------------------------------------------------
function MultiDropZone({
  label,
  accept,
  files,
  onFiles,
  icon,
  hint,
}: {
  label: string;
  accept: string;
  files: File[];
  onFiles: (f: File[]) => void;
  icon: React.ReactNode;
  hint?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = Array.from(e.dataTransfer.files);
    if (dropped.length > 0) onFiles([...files, ...dropped]);
  }, [onFiles, files]);

  const handleSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files || []);
    if (selected.length > 0) onFiles([...files, ...selected]);
    if (inputRef.current) inputRef.current.value = '';
  }, [onFiles, files]);

  const removeFile = useCallback((index: number) => {
    onFiles(files.filter((_, i) => i !== index));
  }, [onFiles, files]);

  return (
    <Paper
      variant="outlined"
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      onClick={() => inputRef.current?.click()}
      sx={{
        p: 3,
        textAlign: 'center',
        cursor: 'pointer',
        borderStyle: 'dashed',
        borderWidth: 1.5,
        borderColor: dragOver ? PC_COLORS.accent : files.length > 0 ? PC_COLORS.okBorder : PC_COLORS.borderStrong,
        backgroundColor: dragOver ? '#F5F8FF' : files.length > 0 ? PC_COLORS.okBg : PC_COLORS.surface2,
        transition: 'all 0.2s',
        '&:hover': { borderColor: PC_COLORS.accent, backgroundColor: '#F5F8FF' },
        minHeight: 148,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1.25,
        borderRadius: '10px',
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        hidden
        multiple
        onChange={handleSelect}
      />
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: '10px',
          border: '1px solid',
          borderColor: files.length > 0 ? PC_COLORS.okBorder : PC_COLORS.border,
          bgcolor: PC_COLORS.surface,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {icon}
      </Box>
      <Typography sx={{ fontSize: '0.84rem', color: PC_COLORS.textSecondary }}>
        {files.length === 0 ? label : `${files.length} fil(er) valda`}
      </Typography>
      {hint && files.length === 0 && (
        <Typography sx={{ fontSize: '0.74rem', color: PC_COLORS.textMuted }}>
          {hint}
        </Typography>
      )}
      {files.map((f, i) => (
        <Chip
          key={`${f.name}-${i}`}
          label={`${f.name} (${(f.size / 1024).toFixed(0)} KB)`}
          size="small"
          variant="outlined"
          onDelete={(e) => { e.stopPropagation(); removeFile(i); }}
          deleteIcon={<DeleteIcon fontSize="small" />}
          sx={{
            bgcolor: PC_COLORS.surface,
            borderColor: PC_COLORS.okBorder,
            color: PC_COLORS.ok,
            maxWidth: '100%',
            '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
            '& .MuiChip-deleteIcon': { color: PC_COLORS.ok },
          }}
        />
      ))}
    </Paper>
  );
}

function fileStableKey(file: File): string {
  return `${file.name}__${file.size}__${file.lastModified}`;
}

function isLegacyDocFile(file: File | { name: string }): boolean {
  return /\.doc$/i.test(file.name) && !/\.docx$/i.test(file.name);
}

function getLegacyDocUploadMessage(fileNames?: string[]): string {
  const prefix = fileNames && fileNames.length > 0
    ? `Gamla Word-filer (.doc) stöds inte här: ${fileNames.join(', ')}.`
    : 'Gamla Word-filer (.doc) stöds inte här.';
  return `${prefix} Spara om filen till .docx och ladda upp den igen. I Word: Arkiv > Spara som > Word-dokument (*.docx).`;
}

function isPdfFile(file: File): boolean {
  return /\.pdf$/i.test(file.name);
}

function buildLabelPdfCropSelectionList(
  files: File[],
  selectionByFile: Record<string, LabelPdfCropSelection | null>,
): Array<LabelPdfCropSelection | null> {
  return files.map((file) => (isPdfFile(file) ? selectionByFile[fileStableKey(file)] ?? null : null));
}

function getCroppedLabelAiInputs(value: {
  labelAiInputs?: Array<{ cropApplied?: boolean; aiFileName?: string | null }> | null;
} | null | undefined): Array<{ cropApplied?: boolean; aiFileName?: string | null }> {
  return (value?.labelAiInputs || []).filter((input) => !!input?.cropApplied);
}

function getCroppedPdfChipLabel(value: {
  labelAiInputs?: Array<{ cropApplied?: boolean; aiFileName?: string | null }> | null;
} | null | undefined): string | null {
  const cropped = getCroppedLabelAiInputs(value);
  if (cropped.length === 0) return null;
  return cropped.length === 1
    ? 'AI-underlag: beskuren PDF'
    : `AI-underlag: ${cropped.length} beskurna PDF-filer`;
}

function getPdfDocumentEntries(value: {
  pdfFileName?: string | null;
  labelAiInputs?: Array<{ originalFileName?: string | null; aiFileName?: string | null }> | null;
} | null | undefined): Array<{ fileName: string; pdfIndex: number | null }> {
  const auditedPdfFiles = (value?.labelAiInputs || [])
    .map((input) => input?.originalFileName || input?.aiFileName || null)
    .filter((fileName): fileName is string => !!fileName && /\.pdf$/i.test(fileName))
    .map((fileName, pdfIndex) => ({ fileName, pdfIndex }));

  if (auditedPdfFiles.length > 0) {
    return auditedPdfFiles;
  }

  return value?.pdfFileName ? [{ fileName: value.pdfFileName, pdfIndex: null }] : [];
}

function LabelPreviewCard({
  file,
  cropBottomPct,
  onCropBottomPctChange,
  pdfCropSelection,
  onOpenPdfCrop,
}: {
  file: File;
  cropBottomPct: number;
  onCropBottomPctChange: (v: number) => void;
  pdfCropSelection?: LabelPdfCropSelection | null;
  onOpenPdfCrop?: () => void;
}) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    setBlobUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  if (!blobUrl) return null;
  const isPdf = isPdfFile(file);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderRadius: '10px',
        borderColor: PC_COLORS.border,
        bgcolor: PC_COLORS.surface,
      }}
    >
      <Typography className="pc-mono" sx={{ fontSize: '0.72rem', fontWeight: 500, display: 'block', mb: 1, color: PC_COLORS.textSecondary }}>
        {file.name}
      </Typography>
      <Box
        sx={{
          position: 'relative',
          height: isPdf ? 520 : 320,
          border: '1px solid',
          borderColor: PC_COLORS.border,
          borderRadius: '8px',
          overflow: 'hidden',
          backgroundColor: PC_COLORS.surface2,
          mb: 1.25,
        }}
      >
        {isPdf ? (
          <PdfPreviewSurface file={file} cropSelection={pdfCropSelection} />
        ) : (
          <Box
            component="img"
            src={blobUrl}
            alt={file.name}
            sx={{ width: '100%', height: '100%', display: 'block', objectFit: 'contain' }}
          />
        )}
        {isPdf && pdfCropSelection?.pageRegions?.length ? (
          <Stack
            spacing={0.75}
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              zIndex: 2,
              pointerEvents: 'none',
            }}
          >
            <Chip
              size="small"
              color="success"
              icon={<CropFreeIcon sx={{ fontSize: '0.95rem !important' }} />}
              label="AI använder beskuren PDF"
              sx={{ bgcolor: 'rgba(46, 125, 50, 0.92)', color: '#fff', '& .MuiChip-icon': { color: '#fff' } }}
            />
            <Chip
              size="small"
              variant="outlined"
              label="Preview visar original med markering"
              sx={{ bgcolor: 'rgba(255,255,255,0.92)', borderColor: '#D1D5DB', color: '#1F2937' }}
            />
          </Stack>
        ) : null}
        {cropBottomPct > 0 && (
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              height: `${cropBottomPct}%`,
              backgroundColor: 'rgba(255, 152, 0, 0.25)',
              borderTop: '2px dashed',
              borderColor: 'warning.main',
              pointerEvents: 'none',
            }}
          />
        )}
      </Box>
      {isPdf ? (
        <Stack spacing={1}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Chip
              size="small"
              variant={pdfCropSelection?.pageRegions?.length ? 'filled' : 'outlined'}
              color={pdfCropSelection?.pageRegions?.length ? 'success' : 'default'}
              label={pdfCropSelection?.pageRegions?.length
                ? `${pdfCropSelection.pageRegions.length} sida/sidor beskurna`
                : 'Ingen exakt beskärning vald'}
            />
            <Button size="small" variant="outlined" onClick={onOpenPdfCrop}>
              Öppna beskärningspopup
            </Button>
          </Stack>
          <Typography sx={{ fontSize: '0.72rem', color: PC_COLORS.textSecondary }}>
            {pdfCropSelection?.pageRegions?.length
              ? `Originalfilen visas ovan för referens med grön beskärningsmarkering. AI får den beskurna PDF-versionen från popupen (${pdfCropSelection.pageRegions.map((region) => `sida ${region.pageNumber}`).join(', ')}).`
              : 'PDF-etiketter beskärs med exakt rektangel i popupen. Originalfilen sparas, men en beskuren PDF skickas till AI när beskärning finns.'}
          </Typography>
        </Stack>
      ) : (
        <>
          <Typography sx={{ fontSize: '0.72rem', color: PC_COLORS.textSecondary }}>
            Beskär nedre del: {cropBottomPct}%
          </Typography>
          <Slider
            size="small"
            min={0}
            max={40}
            step={1}
            value={cropBottomPct}
            onChange={(_e, v) => onCropBottomPctChange(Number(v))}
            valueLabelDisplay="auto"
            sx={{ color: PC_COLORS.accent }}
          />
        </>
      )}
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Allergen highlight — renders allergen words in bold within ingredient text
// ---------------------------------------------------------------------------
function stripMarkdownMarkers(text: string): string {
  return text.replace(/\*\*/g, '');
}

function AllergenHighlight({ text, allergens }: { text: string; allergens: string | null }) {
  if (!text) return <>{'Saknas'}</>;
  if (text.includes('**')) {
    const parts = text.split(/(\*\*[\s\S]+?\*\*)/g);
    return (
      <>
        {parts.map((part, i) => {
          const match = part.match(/^\*\*([\s\S]+)\*\*$/);
          if (match) return <strong key={i}>{match[1]}</strong>;
          return <span key={i}>{part}</span>;
        })}
      </>
    );
  }
  if (!allergens?.trim()) return <>{text}</>;

  const items = allergens
    .split(/,\s*|\s+och\s+/i)
    .map(s => s.trim())
    .filter(Boolean);
  if (items.length === 0) return <>{text}</>;

  const sorted = Array.from(new Set(items)).sort((a, b) => b.length - a.length);
  const escaped = sorted.map(s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const pattern = new RegExp(`(${escaped.join('|')})`, 'gi');

  const parts = text.split(pattern);
  const lowerSet = new Set(sorted.map(s => s.toLowerCase()));

  return (
    <>
      {parts.map((part, i) =>
        lowerSet.has(part.toLowerCase())
          ? <strong key={i}>{part}</strong>
          : <span key={i}>{part}</span>
      )}
    </>
  );
}

function parseAllergenTerms(text: string | null): string[] {
  if (!text?.trim()) return [];
  return text
    .split(/\s*(?:,\s*|;\s*|\n+|\s+och\s+|\s+and\s+)\s*/i)
    .map((item) => item.trim())
    .filter(Boolean);
}

function normalizeDiffToken(token: string): string {
  return stripMarkdownMarkers(token)
    .toLowerCase()
    .replace(/(\d)\s+%/g, '$1%')
    .replace(/^[\s"'“”`´‘’.,;:!?()[\]{}]+|[\s"'“”`´‘’.,;:!?()[\]{}]+$/g, '')
    .trim();
}

function splitPlainTokenByAllergens(token: string, allergens: string | null): Array<{ text: string; bold: boolean }> {
  const items = Array.from(new Set(parseAllergenTerms(allergens).map((item) => item.toLowerCase())));
  if (items.length === 0) {
    return [{ text: token, bold: false }];
  }

  const escaped = items
    .slice()
    .sort((a, b) => b.length - a.length)
    .map((item) => item.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const pattern = new RegExp(`(${escaped.join('|')})`, 'gi');

  return token
    .split(pattern)
    .filter((part) => part.length > 0)
    .map((part) => ({ text: part, bold: items.includes(part.toLowerCase()) }));
}

function splitTokenIntoStyledParts(token: string, allergens: string | null): Array<{ text: string; bold: boolean }> {
  if (!token.includes('**')) {
    return splitPlainTokenByAllergens(token, allergens);
  }

  return token
    .split(/(\*\*[\s\S]+?\*\*)/g)
    .filter((part) => part.length > 0)
    .flatMap((part) => {
      const match = part.match(/^\*\*([\s\S]+)\*\*$/);
      if (match) {
        return [{ text: match[1], bold: true }];
      }
      return splitPlainTokenByAllergens(part, allergens);
    });
}

function IngredientDiffHighlight({ text, reference, allergens }: { text: string; reference: string | null; allergens: string | null }) {
  if (!text) return <>{'Saknas'}</>;
  if (!reference) return <AllergenHighlight text={text} allergens={allergens} />;

  const words = text.split(/(\s+)/);
  const refWords = new Set(reference.split(/(\s+)/).map(normalizeDiffToken).filter(Boolean));
  const normalizeWhole = (value: string) => value.split(/(\s+)/).map(normalizeDiffToken).filter(Boolean).join(' ');

  if (normalizeWhole(text) === normalizeWhole(reference)) {
    return <AllergenHighlight text={text} allergens={allergens} />;
  }

  return (
    <>
      {words.map((word, i) => {
        if (/^\s+$/.test(word)) return <span key={i}>{word}</span>;

        const diffToken = normalizeDiffToken(word);
        const isDiff = !!diffToken && !refWords.has(diffToken);
        const parts = splitTokenIntoStyledParts(word, allergens);

        return (
          <span
            key={i}
            style={isDiff ? { backgroundColor: '#fff3cd', borderRadius: 2, padding: '0 2px' } : undefined}
          >
            {parts.map((part, j) => (
              part.bold ? <strong key={j}>{part.text}</strong> : <span key={j}>{part.text}</span>
            ))}
          </span>
        );
      })}
    </>
  );
}

function AllergenListHighlight({ text }: { text: string }) {
  if (!text) return <>{'Saknas'}</>;

  const parts = text.split(/(\s*,\s*|\s+och\s+|\s+and\s+|\n+)/i);
  return (
    <>
      {parts.map((part, i) => {
        if (!part) return null;
        if (/^\s*(?:,\s*|\s+och\s+|\s+and\s+|\n+)\s*$/i.test(part)) {
          return <span key={i}>{part}</span>;
        }
        return (
          <span
            key={i}
            style={{ backgroundColor: '#fff3cd', fontWeight: 700, borderRadius: 2, padding: '0 2px' }}
          >
            {part.trim()}
          </span>
        );
      })}
    </>
  );
}

// ---------------------------------------------------------------------------
// Word-level diff helper — highlights words that differ from reference
// ---------------------------------------------------------------------------
function DiffHighlight({ text, reference }: { text: string; reference: string | null }) {
  if (!text || !reference) {
    return <>{text || 'Saknas'}</>;
  }
  // Normalize for comparison: collapse whitespace, and strip spaces before % ("70 %" → "70%")
  const normalize = (s: string) => s.toLowerCase().replace(/(\d)\s+%/g, '$1%').replace(/\s+/g, ' ').trim();
  if (normalize(text) === normalize(reference)) {
    return <>{text}</>;
  }

  const normalizeWord = (w: string) => w.toLowerCase().replace(/(\d)\s+%/g, '$1%');
  const wordsA = text.split(/(\s+)/);
  const refWords = new Set(reference.toLowerCase().replace(/(\d)\s+%/g, '$1%').split(/\s+/).filter(Boolean));

  return (
    <>
      {wordsA.map((word, i) => {
        if (/^\s+$/.test(word)) return <span key={i}>{word}</span>;
        const isInRef = refWords.has(normalizeWord(word));
        if (isInRef) return <span key={i}>{word}</span>;
        return (
          <span key={i} style={{ backgroundColor: '#fff3cd', fontWeight: 600, borderRadius: 2, padding: '0 2px' }}>
            {word}
          </span>
        );
      })}
    </>
  );
}

// ---------------------------------------------------------------------------
// Nutrition number diff — compares only the numbers, ignores language
// ---------------------------------------------------------------------------
type NutritionKey =
  | 'energy_kj'
  | 'energy_kcal'
  | 'fat'
  | 'saturated_fat'
  | 'monounsaturated_fat'
  | 'polyunsaturated_fat'
  | 'carbohydrate'
  | 'sugars'
  | 'fiber'
  | 'protein'
  | 'salt';

const NUTRITION_LABELS_SV: Record<NutritionKey, string> = {
  energy_kj: 'Energi kJ',
  energy_kcal: 'Energi kcal',
  fat: 'Fett',
  saturated_fat: 'mättat fett',
  monounsaturated_fat: 'enkelomättat fett',
  polyunsaturated_fat: 'fleromättat fett',
  carbohydrate: 'Kolhydrat',
  sugars: 'sockerarter',
  fiber: 'Fiber',
  protein: 'Protein',
  salt: 'Salt',
};

const NUTRITION_PATTERNS: Record<NutritionKey, RegExp[]> = {
  energy_kj: [/(\d+(?:\.\d+)?)\s*kj\b/i, /(?:energi|energy)[^0-9]{0,30}kj[^0-9]{0,20}(\d+(?:\.\d+)?)/i],
  energy_kcal: [/(\d+(?:\.\d+)?)\s*kcal\b/i, /(?:energi|energy)[^0-9]{0,30}kcal[^0-9]{0,20}(\d+(?:\.\d+)?)/i],
  fat: [/(?:^|[\n,;|.])\s*(?:fett|total\s*fat|fat)\b[^0-9]{0,25}(\d+(?:\.\d+)?)/i],
  saturated_fat: [/(?:varav\s+)?(?:m[äa]ttat(?:\s*fett)?|saturated\s*fat)[^0-9]{0,25}(\d+(?:\.\d+)?)/i],
  monounsaturated_fat: [/(?:varav\s+)?(?:enkelom[äa]ttat(?:\s*fett)?|monounsaturated\s*fat)[^0-9]{0,25}(\d+(?:\.\d+)?)/i],
  polyunsaturated_fat: [/(?:varav\s+)?(?:flerom[äa]ttat(?:\s*fett)?|polyunsaturated\s*fat)[^0-9]{0,25}(\d+(?:\.\d+)?)/i],
  carbohydrate: [/(?:kolhydrat|carbohydrate)[^0-9]{0,25}(\d+(?:\.\d+)?)/i],
  sugars: [/(?:sockerarter|sugars?)[^0-9]{0,25}(\d+(?:\.\d+)?)/i],
  fiber: [/(?:fiber|fibre)[^0-9]{0,25}(\d+(?:\.\d+)?)/i],
  protein: [/(?:^|[\n,;|.])\s*protein\b[^0-9]{0,25}(\d+(?:\.\d+)?)/i],
  salt: [/(?:^|[\n,;|.])\s*salt\b[^0-9]{0,25}(\d+(?:\.\d+)?)/i],
};

function extractNutritionNumbers(s: string): number[] {
  const normalized = s.replace(/,/g, '.');
  const matches = normalized.match(/\d+\.?\d*/g);
  return matches ? matches.map(m => parseFloat(m)) : [];
}

function parseNutritionMap(text: string): Partial<Record<NutritionKey, number>> {
  const out: Partial<Record<NutritionKey, number>> = {};
  const normalized = text.replace(/(\d),(\d)/g, '$1.$2');
  const keys = Object.keys(NUTRITION_PATTERNS) as NutritionKey[];
  for (const key of keys) {
    for (const pattern of NUTRITION_PATTERNS[key]) {
      const m = normalized.match(pattern);
      if (!m) continue;
      const v = parseFloat(m[1]);
      if (Number.isFinite(v)) {
        out[key] = v;
        break;
      }
    }
  }
  return out;
}

function formatNutritionInline(text: string): string {
  return text
    .replace(/\s*\|\s*/g, ': ')
    .replace(/\s*\n+\s*/g, ', ')
    .replace(/\s{2,}/g, ' ')
    .replace(/\s+,/g, ',')
    .trim();
}

function NutritionDiff({ text, reference }: { text: string; reference: string | null }) {
  if (!text || !reference) return <>{text || 'Saknas'}</>;
  const mapText = parseNutritionMap(text);
  const mapRef = parseNutritionMap(reference);
  const keysText = Object.keys(mapText) as NutritionKey[];
  const keysRef = Object.keys(mapRef) as NutritionKey[];

  let diffs: { label: string; a: number | undefined; b: number | undefined }[] = [];
  let orderChangedOnly = false;

  if (keysText.length > 0 && keysRef.length > 0) {
    const allKeys = new Set<NutritionKey>([...keysText, ...keysRef]);
    Array.from(allKeys).forEach((key: NutritionKey) => {
      const va = mapRef[key];
      const vb = mapText[key];
      if (va === undefined || vb === undefined || Math.abs(va - vb) >= 0.01) {
        diffs.push({ label: NUTRITION_LABELS_SV[key], a: va, b: vb });
      }
    });
    if (diffs.length === 0) {
      const orderA = extractNutritionNumbers(reference);
      const orderB = extractNutritionNumbers(text);
      orderChangedOnly = orderA.length === orderB.length && orderA.some((n, i) => Math.abs(n - orderB[i]) >= 0.01);
    }
  } else {
    const numsText = extractNutritionNumbers(text);
    const numsRef = extractNutritionNumbers(reference);
    if (numsText.length === 0 || numsRef.length === 0) {
      return <DiffHighlight text={text} reference={reference} />;
    }
    const len = Math.max(numsText.length, numsRef.length);
    for (let i = 0; i < len; i++) {
      const va = i < numsRef.length ? numsRef[i] : undefined;
      const vb = i < numsText.length ? numsText[i] : undefined;
      if (va === undefined || vb === undefined || Math.abs(va - vb) >= 0.01) {
        diffs.push({ label: `Värde ${i + 1}`, a: va, b: vb });
      }
    }
  }

  if (diffs.length === 0) {
    return (
      <span style={{ color: '#2e7d32' }}>
        {formatNutritionInline(text)}{' '}
        <span style={{ fontSize: '0.85em', opacity: 0.7 }}>
          {orderChangedOnly ? '(samma värden, annan ordning)' : '(siffrorna matchar)'}
        </span>
      </span>
    );
  }

  return (
    <Box>
      <Typography variant="body2" sx={{ whiteSpace: 'normal' }}>{formatNutritionInline(text)}</Typography>
      {diffs.length > 0 && (
        <Box sx={{ mt: 1, p: 1, backgroundColor: '#fff3cd', borderRadius: 1, border: '1px solid #ffe082' }}>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>Sifferavvikelser:</Typography>
          {diffs.map((d, i) => (
            <Typography key={i} variant="caption" component="div" sx={{ ml: 1 }}>
              {d.label}: DAM <strong>{d.a ?? 'saknas'}</strong> vs denna källa <strong>{d.b ?? 'saknas'}</strong>
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
}

const NUTRITION_FIELD_KEYS = new Set(['naringsvarden_per_100g']);
const WEIGHT_FIELD_KEYS = new Set(['nettovikt', 'avrunnen_vikt']);

// Parse a weight string to grams — mirrors backend logic
function parseWeightGrams(s: string): number | null {
  if (!s) return null;
  const norm = s.toLowerCase().replace(/,/g, '.').replace(/\s+/g, '');
  // Handle multiplication expressions: "4x55g", "4×55g", "4*55g"
  const mulM = norm.match(/^(\d+\.?\d*)[x×*](\d+\.?\d*)\s*(kg|g(?:ram)?)?/);
  if (mulM) {
    const product = parseFloat(mulM[1]) * parseFloat(mulM[2]);
    return mulM[3]?.startsWith('kg') ? product * 1000 : product;
  }
  const kgM = norm.match(/(\d+\.?\d*)kg/);
  if (kgM) return parseFloat(kgM[1]) * 1000;
  const gM = norm.match(/(\d+\.?\d*)g/);
  if (gM) return parseFloat(gM[1]);
  const numM = norm.match(/^(\d+\.?\d*)$/);
  if (numM) return parseFloat(numM[1]);
  return null;
}

function WeightDiff({ text, reference }: { text: string; reference: string | null }) {
  if (!text) return <em style={{ color: '#999' }}>Saknas</em>;
  const textG = parseWeightGrams(text);
  const refG = reference ? parseWeightGrams(reference) : null;
  const matches = textG !== null && refG !== null && Math.abs(textG - refG) < 0.5;
  if (matches) {
    return <span style={{ color: '#2e7d32' }}>{text} <span style={{ fontSize: '0.85em', opacity: 0.7 }}>(vikten matchar)</span></span>;
  }
  return (
    <Box>
      <Typography variant="body2">{text}</Typography>
      {textG !== null && refG !== null && (
        <Box sx={{ mt: 1, p: 1, backgroundColor: '#fff3cd', borderRadius: 1, border: '1px solid #ffe082' }}>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>Viktavvikelse:</Typography>
          <Typography variant="caption" component="div" sx={{ ml: 1 }}>
            DAM <strong>{refG} g</strong> vs denna källa <strong>{textG} g</strong>
          </Typography>
        </Box>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Raw text display component
// ---------------------------------------------------------------------------
function RawTextSection({ rawLabelText, rawSupplierText }: { rawLabelText?: string | null; rawSupplierText?: string | null }) {
  const [labelOpen, setLabelOpen] = React.useState(false);
  const [supplierOpen, setSupplierOpen] = React.useState(false);

  if (!rawLabelText && !rawSupplierText) return null;

  return (
    <Box sx={{ mt: 3 }}>
      <Typography sx={{ fontSize: '0.84rem', fontWeight: 600, mb: 1, color: PC_COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        Rå extraherad text (för verifiering)
      </Typography>
      <Stack spacing={1}>
        {rawLabelText && (
          <Paper variant="outlined" sx={{ overflow: 'hidden', borderRadius: '10px', borderColor: PC_COLORS.border, bgcolor: PC_COLORS.surface }}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.25, cursor: 'pointer', bgcolor: PC_COLORS.surface2, '&:hover': { bgcolor: '#F1F0EC' } }}
              onClick={() => setLabelOpen(v => !v)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PictureAsPdfIcon fontSize="small" color="error" />
                <Typography sx={{ fontSize: '0.83rem', fontWeight: 500, color: PC_COLORS.textPrimary }}>Etikett (PDF) — transkriberad text</Typography>
              </Box>
              {labelOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </Box>
            <Collapse in={labelOpen}>
              <Box sx={{ p: 2, borderTop: '1px solid', borderColor: PC_COLORS.border, bgcolor: '#FAFAF8' }}>
                <Typography
                  component="pre"
                  sx={{ fontFamily: 'var(--pc-mono)', fontSize: '0.78rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0, lineHeight: 1.6, color: PC_COLORS.textPrimary }}
                >
                  {rawLabelText}
                </Typography>
              </Box>
            </Collapse>
          </Paper>
        )}
        {rawSupplierText && (
          <Paper variant="outlined" sx={{ overflow: 'hidden', borderRadius: '10px', borderColor: PC_COLORS.border, bgcolor: PC_COLORS.surface }}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.25, cursor: 'pointer', bgcolor: PC_COLORS.surface2, '&:hover': { bgcolor: '#F1F0EC' } }}
              onClick={() => setSupplierOpen(v => !v)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DescriptionIcon fontSize="small" color="primary" />
                <Typography sx={{ fontSize: '0.83rem', fontWeight: 500, color: PC_COLORS.textPrimary }}>Leverantör — extraherad text</Typography>
              </Box>
              {supplierOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </Box>
            <Collapse in={supplierOpen}>
              <Box sx={{ p: 2, borderTop: '1px solid', borderColor: PC_COLORS.border, bgcolor: '#FAFAF8' }}>
                <Typography
                  component="pre"
                  sx={{ fontFamily: 'var(--pc-mono)', fontSize: '0.78rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0, lineHeight: 1.6, color: PC_COLORS.textPrimary }}
                >
                  {rawSupplierText}
                </Typography>
              </Box>
            </Collapse>
          </Paper>
        )}
      </Stack>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Comparison table component
// ---------------------------------------------------------------------------
function ComparisonTable({
  fields,
  resolvedFields,
  onResolve,
}: {
  fields: ComparisonField[];
  resolvedFields?: string[];
  onResolve?: (fieldKey: string) => void;
}) {
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const resolved = new Set(resolvedFields || []);
  const allergenField = fields.find(f => f.fieldKey === 'allergener');
  const allergenList = allergenField?.pdfValue || allergenField?.wordValue || allergenField?.damValue || null;

  const renderCompactValue = (fieldKey: string, value: string | null) => {
    if (!value) {
      return <Typography component="span" sx={{ fontSize: '0.76rem', color: PC_COLORS.textMuted, fontStyle: 'italic' }}>Saknas</Typography>;
    }
    return (
      <Typography
        component="span"
        sx={{
          fontSize: '0.78rem',
          color: PC_COLORS.textSecondary,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: 'block',
          maxWidth: '100%',
        }}
      >
        {NUTRITION_FIELD_KEYS.has(fieldKey) ? formatNutritionInline(value) : stripMarkdownMarkers(value)}
      </Typography>
    );
  };

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '10px', borderColor: PC_COLORS.border, bgcolor: PC_COLORS.surface, overflow: 'hidden' }}>
      <Table size="small" sx={{ tableLayout: 'fixed' }}>
        <TableHead>
          <TableRow sx={{ backgroundColor: PC_COLORS.surface2 }}>
            <TableCell sx={{ width: 40, fontSize: '0.68rem', fontWeight: 600, color: PC_COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `2px solid ${PC_COLORS.border}` }}>#</TableCell>
            <TableCell sx={{ fontSize: '0.68rem', fontWeight: 600, color: PC_COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `2px solid ${PC_COLORS.border}` }}>Fält</TableCell>
            <TableCell sx={{ fontSize: '0.68rem', fontWeight: 600, color: PC_COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `2px solid ${PC_COLORS.border}`, borderTop: `3px solid ${SOURCE_META.dam.accent}` }}>DAM / Dabas</TableCell>
            <TableCell sx={{ fontSize: '0.68rem', fontWeight: 600, color: PC_COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `2px solid ${PC_COLORS.border}`, borderTop: `3px solid ${SOURCE_META.pdf.accent}` }}>Etikett (PDF)</TableCell>
            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, fontSize: '0.68rem', fontWeight: 600, color: PC_COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `2px solid ${PC_COLORS.border}`, borderTop: `3px solid ${SOURCE_META.word.accent}` }}>Leverantör (Word)</TableCell>
            <TableCell sx={{ width: 150, fontSize: '0.68rem', fontWeight: 600, color: PC_COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `2px solid ${PC_COLORS.border}` }}>Status</TableCell>
            <TableCell sx={{ width: 44, borderBottom: `2px solid ${PC_COLORS.border}` }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {fields.map((f) => {
            const isExpanded = expandedRow === f.fieldKey;
            const isResolved = resolved.has(f.fieldKey);
            const hasRegulatory = f.regulatoryFlags.length > 0;

            return (
              <React.Fragment key={f.fieldKey}>
                <TableRow
                  hover
                  onClick={() => setExpandedRow(isExpanded ? null : f.fieldKey)}
                  sx={{
                    cursor: 'pointer',
                    opacity: isResolved ? 0.55 : 1,
                    backgroundColor: f.status === 'mismatch' || hasRegulatory ? '#FFFBFB' : undefined,
                    '&:hover': { backgroundColor: PC_COLORS.surface2 },
                  }}
                >
                  <TableCell>
                    <Typography className="pc-mono" sx={{ fontSize: '0.75rem', color: PC_COLORS.textMuted }}>{f.priority}</Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: '5px',
                          border: '1px solid',
                          borderColor: PC_COLORS.border,
                          bgcolor: PC_COLORS.surface2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.82rem',
                          flexShrink: 0,
                        }}
                      >
                        {getFieldIcon(f.fieldKey)}
                      </Box>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 500, color: PC_COLORS.textPrimary }}>{f.label}</Typography>
                      {f.comparisonNote && (
                        <Tooltip title={f.comparisonNote}>
                          <Box
                            sx={{
                              px: 0.9,
                              py: 0.15,
                              borderRadius: '999px',
                              border: '1px solid',
                              borderColor: '#BFDBFE',
                              bgcolor: '#EFF6FF',
                              color: '#1E3A5F',
                              fontSize: '0.63rem',
                              fontWeight: 600,
                            }}
                          >
                            Notering
                          </Box>
                        </Tooltip>
                      )}
                      {hasRegulatory && (
                        <Tooltip title="Regulatorisk varning">
                          <WarningIcon fontSize="small" sx={{ color: PC_COLORS.crit }} />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ py: 1.5 }}>
                    {renderCompactValue(f.fieldKey, f.damValue)}
                  </TableCell>
                  <TableCell sx={{ py: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <ConfidenceDot level={f.pdfConfidence} />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        {renderCompactValue(f.fieldKey, f.pdfValue)}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ py: 1.5, display: { xs: 'none', md: 'table-cell' } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <ConfidenceDot level={f.wordConfidence} />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        {renderCompactValue(f.fieldKey, f.wordValue)}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={f.status} resolved={isResolved} />
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        color: PC_COLORS.textMuted,
                        fontSize: '0.72rem',
                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s',
                      }}
                    >
                      ▼
                    </Box>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={7} sx={{ py: 0, borderBottom: isExpanded ? undefined : 'none' }}>
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <Box sx={{ py: 2.5, px: { xs: 2, sm: 3 }, bgcolor: '#FAFAF8' }}>
                        <Stack spacing={2}>
                          {f.status === 'mismatch' && (
                            <Alert severity="warning" variant="outlined" sx={{ py: 0.5, borderColor: '#F9C06A', bgcolor: '#FEF3E2' }}>
                              {NUTRITION_FIELD_KEYS.has(f.fieldKey)
                                ? 'Siffrorna jämförs — språk och formatering ignoreras.'
                                : WEIGHT_FIELD_KEYS.has(f.fieldKey)
                                  ? 'Vikten jämförs — g och kg konverteras automatiskt.'
                                  : f.fieldKey === 'innehallsdeklaration'
                                    ? 'Ingredienstexten visas exakt som på källan. Fetstil visas som på etiketten i stället för att gissas fram i efterhand.'
                                  : <>Ord markerade med <span style={{ backgroundColor: '#fff3cd', fontWeight: 600, padding: '0 3px', borderRadius: 2 }}>gul bakgrund</span> skiljer sig från DAM/Dabas-värdet.</>
                              }
                            </Alert>
                          )}
                          {f.comparisonNote && (
                            <Alert severity="info" variant="outlined" sx={{ py: 0.5, borderColor: '#BFDBFE', bgcolor: '#EFF6FF' }}>
                              {f.comparisonNote}
                            </Alert>
                          )}
                          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5 }}>
                            <Box sx={{ border: '1px solid', borderColor: PC_COLORS.border, borderRadius: '8px', overflow: 'hidden', bgcolor: PC_COLORS.surface }}>
                              <Box sx={{ px: 1.75, py: 1, display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid', borderColor: PC_COLORS.border, bgcolor: SOURCE_META.dam.softBg, color: SOURCE_META.dam.softText, fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                <Box component="span">{SOURCE_META.dam.icon}</Box>
                                <Box component="span">{SOURCE_META.dam.label}</Box>
                              </Box>
                              <Typography component="div" sx={{ whiteSpace: 'pre-wrap', px: 1.75, py: 1.5, fontSize: '0.82rem', lineHeight: 1.55, color: PC_COLORS.textPrimary }}>
                                {f.damValue
                                  ? (NUTRITION_FIELD_KEYS.has(f.fieldKey)
                                      ? formatNutritionInline(f.damValue)
                                      : f.fieldKey === 'innehallsdeklaration'
                                        ? <AllergenHighlight text={f.damValue} allergens={allergenList} />
                                        : f.damValue)
                                  : <em style={{ color: '#999' }}>Saknas</em>
                                }
                              </Typography>
                            </Box>
                            <Box sx={{ border: '1px solid', borderColor: PC_COLORS.border, borderRadius: '8px', overflow: 'hidden', bgcolor: PC_COLORS.surface }}>
                              <Box sx={{ px: 1.75, py: 1, display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid', borderColor: PC_COLORS.border, bgcolor: SOURCE_META.pdf.softBg, color: SOURCE_META.pdf.softText, fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                <Box component="span">{SOURCE_META.pdf.icon}</Box>
                                <Box component="span">{SOURCE_META.pdf.label}</Box>
                                <ConfidenceDot level={f.pdfConfidence} />
                              </Box>
                              <Typography component="div" sx={{ whiteSpace: 'pre-wrap', px: 1.75, py: 1.5, fontSize: '0.82rem', lineHeight: 1.55, color: PC_COLORS.textPrimary }}>
                                {f.status === 'mismatch' && f.damValue ? (
                                  NUTRITION_FIELD_KEYS.has(f.fieldKey)
                                    ? <NutritionDiff text={f.pdfValue || ''} reference={f.damValue} />
                                    : WEIGHT_FIELD_KEYS.has(f.fieldKey)
                                      ? <WeightDiff text={f.pdfValue || ''} reference={f.damValue} />
                                      : f.fieldKey === 'allergener'
                                        ? <AllergenListHighlight text={f.pdfValue || ''} />
                                      : f.fieldKey === 'innehallsdeklaration'
                                        ? <IngredientDiffHighlight text={f.pdfValue || ''} reference={f.damValue} allergens={allergenList} />
                                        : <DiffHighlight text={f.pdfValue || ''} reference={f.damValue} />
                                ) : (
                                  (f.pdfValue
                                    ? (NUTRITION_FIELD_KEYS.has(f.fieldKey)
                                        ? formatNutritionInline(f.pdfValue)
                                        : f.fieldKey === 'allergener'
                                          ? <AllergenListHighlight text={f.pdfValue} />
                                        : f.fieldKey === 'innehallsdeklaration'
                                          ? <AllergenHighlight text={f.pdfValue} allergens={allergenList} />
                                          : f.pdfValue)
                                    : <em style={{ color: '#999' }}>Saknas</em>)
                                )}
                              </Typography>
                            </Box>
                            <Box sx={{ border: '1px solid', borderColor: PC_COLORS.border, borderRadius: '8px', overflow: 'hidden', bgcolor: PC_COLORS.surface }}>
                              <Box sx={{ px: 1.75, py: 1, display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid', borderColor: PC_COLORS.border, bgcolor: SOURCE_META.word.softBg, color: SOURCE_META.word.softText, fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                <Box component="span">{SOURCE_META.word.icon}</Box>
                                <Box component="span">{SOURCE_META.word.label}</Box>
                                <ConfidenceDot level={f.wordConfidence} />
                              </Box>
                              <Typography component="div" sx={{ whiteSpace: 'pre-wrap', px: 1.75, py: 1.5, fontSize: '0.82rem', lineHeight: 1.55, color: PC_COLORS.textPrimary }}>
                                {f.status === 'mismatch' && f.damValue ? (
                                  NUTRITION_FIELD_KEYS.has(f.fieldKey)
                                    ? <NutritionDiff text={f.wordValue || ''} reference={f.damValue} />
                                    : WEIGHT_FIELD_KEYS.has(f.fieldKey)
                                      ? <WeightDiff text={f.wordValue || ''} reference={f.damValue} />
                                      : f.fieldKey === 'allergener'
                                        ? <AllergenListHighlight text={f.wordValue || ''} />
                                      : f.fieldKey === 'innehallsdeklaration'
                                        ? <IngredientDiffHighlight text={f.wordValue || ''} reference={f.damValue} allergens={allergenList} />
                                        : <DiffHighlight text={f.wordValue || ''} reference={f.damValue} />
                                ) : (
                                  (f.wordValue
                                    ? (NUTRITION_FIELD_KEYS.has(f.fieldKey)
                                        ? formatNutritionInline(f.wordValue)
                                        : f.fieldKey === 'allergener'
                                          ? <AllergenListHighlight text={f.wordValue} />
                                        : f.fieldKey === 'innehallsdeklaration'
                                          ? <AllergenHighlight text={f.wordValue} allergens={allergenList} />
                                          : f.wordValue)
                                    : <em style={{ color: '#999' }}>Saknas</em>)
                                )}
                              </Typography>
                            </Box>
                          </Box>
                          {f.regulatoryFlags.length > 0 && (
                            <Alert severity="error" variant="outlined" sx={{ borderColor: PC_COLORS.critBorder, bgcolor: PC_COLORS.critBg }}>
                              Regulatoriska flaggor: {f.regulatoryFlags.join(', ')}
                            </Alert>
                          )}
                          {onResolve && !isResolved && f.status !== 'match' && (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<CheckIcon />}
                              onClick={(e) => { e.stopPropagation(); onResolve(f.fieldKey); }}
                              sx={{ alignSelf: 'flex-start', borderColor: PC_COLORS.borderStrong, color: PC_COLORS.textPrimary, textTransform: 'none' }}
                            >
                              Markera som hanterad
                            </Button>
                          )}
                        </Stack>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function getV2StatusAppearance(status: V2RowStatus) {
  switch (status) {
    case 'match':
      return { label: 'Överensstämmer', color: PC_COLORS.ok, bg: PC_COLORS.okBg, border: PC_COLORS.okBorder };
    case 'minor_difference':
      return { label: 'Mindre skillnad', color: '#92400E', bg: '#FEF3E2', border: '#F9C06A' };
    case 'mismatch':
      return { label: 'Avvikelse', color: PC_COLORS.crit, bg: PC_COLORS.critBg, border: PC_COLORS.critBorder };
    default:
      return { label: 'Saknas', color: PC_COLORS.textMuted, bg: PC_COLORS.bg, border: PC_COLORS.border };
  }
}

function V2StatusBadge({ status }: { status: V2RowStatus }) {
  const appearance = getV2StatusAppearance(status);
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: 1.25,
        py: 0.5,
        borderRadius: '999px',
        border: '1px solid',
        borderColor: appearance.border,
        bgcolor: appearance.bg,
        color: appearance.color,
        fontSize: '0.72rem',
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      {appearance.label}
    </Box>
  );
}

function flattenV2Rows(v2Result: V2ComparisonResultData): V2ComparisonRow[] {
  return [
    ...v2Result.sections.ingredients,
    ...v2Result.sections.nutritionPer100g,
    ...v2Result.sections.allergens,
    ...v2Result.sections.productInfo,
  ];
}

function V2ScoreBar({ v2Result }: { v2Result: V2ComparisonResultData }) {
  const rows = flattenV2Rows(v2Result);
  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 2, sm: '16px 24px' },
        mb: 2.5,
        borderRadius: '10px',
        borderColor: PC_COLORS.border,
        bgcolor: PC_COLORS.surface,
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' },
        }}
      >
        <SummaryStatCard
          value={rows.filter((row) => row.status === 'match').length}
          label="Överensstämmer"
          subtitle="Rader som matchar"
          tone="ok"
        />
        <SummaryStatCard
          value={rows.filter((row) => row.status === 'minor_difference').length}
          label="Mindre skillnader"
          subtitle="Samma sak, annan form"
          tone="warn"
        />
        <SummaryStatCard
          value={rows.filter((row) => row.status === 'mismatch').length}
          label="Avvikelser"
          subtitle="Faktiska skillnader"
          tone="warn"
        />
        <SummaryStatCard
          value={rows.filter((row) => row.status === 'missing').length}
          label="Saknas"
          subtitle="Behöver verifieras"
          tone="miss"
        />
      </Box>
    </Paper>
  );
}

function formatV2SourceMode(mode: string): string {
  switch (mode) {
    case 'pdf_document':
      return 'PDF direkt till modellen';
    case 'image':
      return 'Bild direkt till modellen';
    case 'mixed':
      return 'Blandade etikettformat';
    case 'word_text_fallback':
      return 'Word som extraherad text';
    case 'text':
      return 'Text';
    default:
      return 'Ej uppladdad';
  }
}

function renderV2Value(value: string | null) {
  if (!value) {
    return <Typography component="span" sx={{ fontSize: '0.78rem', color: PC_COLORS.textMuted, fontStyle: 'italic' }}>Saknas</Typography>;
  }

  return (
    <Typography
      component="span"
      sx={{
        fontSize: '0.8rem',
        color: PC_COLORS.textPrimary,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}
    >
      {stripMarkdownMarkers(value)}
    </Typography>
  );
}

function isV2EffectivelyEmptyValue(value: string | null): boolean {
  if (!value?.trim()) return true;
  const normalized = value.trim().toLowerCase();
  return normalized === 'ej angivet'
    || normalized === 'not specified'
    || normalized === 'n/a'
    || normalized === 'na'
    || normalized === '-'
    || normalized === 'saknas'
    || normalized === 'none'
    || normalized === 'null';
}

function rowHasActualV2Value(row: V2ComparisonRow): boolean {
  return [row.dabas, row.labelDoc, row.supplier].some((value) => !isV2EffectivelyEmptyValue(value));
}

function getMatchedValueRowBackground(row: V2ComparisonRow): string | undefined {
  if (row.status === 'mismatch') return '#FFFBFB';
  if (row.status === 'minor_difference') return '#FEF3E2';
  if (row.status === 'match' && rowHasActualV2Value(row)) return '#ECFDF5';
  return undefined;
}

function V2SectionTable({
  sectionKey,
  title,
  subtitle,
  rows,
  resolvedFields,
  onResolve,
}: {
  sectionKey: 'ingredients' | 'nutritionPer100g' | 'allergens' | 'productInfo';
  title: string;
  subtitle?: string;
  rows: V2ComparisonRow[];
  resolvedFields?: string[];
  onResolve?: (fieldKey: string) => void;
}) {
  const [open, setOpen] = useState(true);
  const resolved = new Set(resolvedFields || []);

  return (
    <Box sx={{ mb: 2.5 }}>
      <Paper variant="outlined" sx={{ borderRadius: '10px', borderColor: PC_COLORS.border, bgcolor: PC_COLORS.surface, overflow: 'hidden' }}>
        <Box
          onClick={() => setOpen((current) => !current)}
          sx={{
            px: 2,
            py: 1.5,
            cursor: 'pointer',
            borderBottom: open ? '1px solid' : 'none',
            borderColor: PC_COLORS.border,
            bgcolor: PC_COLORS.surface,
            '&:hover': { bgcolor: PC_COLORS.surface2 },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
            <Box>
              <Typography sx={{ fontSize: '0.84rem', fontWeight: 600, color: PC_COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {title}
              </Typography>
              {subtitle && (
                <Typography sx={{ fontSize: '0.8rem', color: PC_COLORS.textSecondary, mt: 0.35 }}>
                  {subtitle}
                </Typography>
              )}
            </Box>
            {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </Box>
        </Box>
        <Collapse in={open} timeout="auto">
        <TableContainer component="div" sx={{ overflow: 'hidden' }}>
        <Table size="small" sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: PC_COLORS.surface2 }}>
              <TableCell sx={{ width: 40, fontSize: '0.68rem', fontWeight: 600, color: PC_COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em' }}>#</TableCell>
              <TableCell sx={{ fontSize: '0.68rem', fontWeight: 600, color: PC_COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Fält</TableCell>
              <TableCell sx={{ fontSize: '0.68rem', fontWeight: 600, color: PC_COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', borderTop: `3px solid ${SOURCE_META.dam.accent}` }}>DABAS</TableCell>
              <TableCell sx={{ fontSize: '0.68rem', fontWeight: 600, color: PC_COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', borderTop: `3px solid ${SOURCE_META.pdf.accent}` }}>Etikett</TableCell>
              <TableCell sx={{ fontSize: '0.68rem', fontWeight: 600, color: PC_COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', borderTop: `3px solid ${SOURCE_META.word.accent}` }}>Leverantör</TableCell>
              <TableCell sx={{ width: 150, fontSize: '0.68rem', fontWeight: 600, color: PC_COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Status</TableCell>
              <TableCell sx={{ fontSize: '0.68rem', fontWeight: 600, color: PC_COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Notering</TableCell>
              {onResolve && <TableCell sx={{ width: 170 }} />}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => {
              const isResolved = resolved.has(row.key);
              const rowBackground = sectionKey === 'ingredients'
                ? getMatchedValueRowBackground(row)
                : sectionKey === 'allergens'
                  ? getMatchedValueRowBackground(row)
                  : sectionKey === 'nutritionPer100g'
                    ? getMatchedValueRowBackground(row)
                  : (row.status === 'mismatch' ? '#FFFBFB' : row.status === 'minor_difference' ? '#FEF3E2' : undefined);
              return (
                <TableRow
                  key={row.key}
                  sx={{
                    opacity: isResolved ? 0.55 : 1,
                    backgroundColor: rowBackground,
                  }}
                >
                  <TableCell>
                    <Typography className="pc-mono" sx={{ fontSize: '0.75rem', color: PC_COLORS.textMuted }}>{index + 1}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 500, color: PC_COLORS.textPrimary }}>{row.label}</Typography>
                  </TableCell>
                  <TableCell>{renderV2Value(row.dabas)}</TableCell>
                  <TableCell>{renderV2Value(row.labelDoc)}</TableCell>
                  <TableCell>{renderV2Value(row.supplier)}</TableCell>
                  <TableCell>
                    <V2StatusBadge status={row.status} />
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.78rem', color: PC_COLORS.textSecondary, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {row.note || '—'}
                  </TableCell>
                  {onResolve && (
                    <TableCell>
                      {!isResolved && row.status !== 'match' && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<CheckIcon />}
                          onClick={(e) => { e.stopPropagation(); onResolve(row.key); }}
                          sx={{ borderColor: PC_COLORS.borderStrong, color: PC_COLORS.textPrimary, textTransform: 'none' }}
                        >
                          Markera som hanterad
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        </TableContainer>
        </Collapse>
      </Paper>
    </Box>
  );
}

function V2ComparisonView({
  v2Result,
  resolvedFields,
  onResolve,
  showSourceAuditAlert = true,
}: {
  v2Result: V2ComparisonResultData;
  resolvedFields?: string[];
  onResolve?: (fieldKey: string) => void;
  showSourceAuditAlert?: boolean;
}) {
  return (
    <Box>
      {showSourceAuditAlert && (
        <Alert severity="info" sx={{ mb: 2, borderColor: '#BFDBFE', bgcolor: '#EFF6FF' }} variant="outlined">
          Källa in: etikett = {formatV2SourceMode(v2Result.sourceAudit.labelMode)}, leverantör = {formatV2SourceMode(v2Result.sourceAudit.supplierMode)}.
        </Alert>
      )}
      <V2SectionTable sectionKey="ingredients" title="Ingredienser" rows={v2Result.sections.ingredients} resolvedFields={resolvedFields} onResolve={onResolve} />
      <V2SectionTable
        sectionKey="nutritionPer100g"
        title="Näringsvärde per 100 g"
        subtitle="Obligatorisk information enligt EU-förordning 1169/2011"
        rows={v2Result.sections.nutritionPer100g}
        resolvedFields={resolvedFields}
        onResolve={onResolve}
      />
      <V2SectionTable
        sectionKey="allergens"
        title="Allergener"
        subtitle="Bilaga II, EU-förordning 1169/2011 · 14 listade ämnen"
        rows={v2Result.sections.allergens}
        resolvedFields={resolvedFields}
        onResolve={onResolve}
      />
      <V2SectionTable sectionKey="productInfo" title="Övrig produktinformation" rows={v2Result.sections.productInfo} resolvedFields={resolvedFields} onResolve={onResolve} />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Document viewer dialog
// ---------------------------------------------------------------------------
function DocumentViewerDialog({
  open,
  onClose,
  comparisonId,
  type,
  fileName,
  pdfIndex = null,
}: {
  open: boolean;
  onClose: () => void;
  comparisonId: string;
  type: 'pdf' | 'word';
  fileName: string | null;
  pdfIndex?: number | null;
}) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isImageDoc = !!fileName && /\.(png|jpe?g)$/i.test(fileName);

  useEffect(() => {
    if (!open || !comparisonId) return;
    setLoading(true);
    setError(null);
    setBlobUrl(null);
    let objectUrl: string | null = null;

    axiosInstance.get(getDocumentUrl(comparisonId, type, pdfIndex), { responseType: 'blob' })
      .then(res => {
        objectUrl = URL.createObjectURL(res.data);
        setBlobUrl(objectUrl);
      })
      .catch(err => setError(err?.response?.data?.message || 'Kunde inte ladda dokumentet.'))
      .finally(() => setLoading(false));

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [open, comparisonId, type, pdfIndex]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {type === 'pdf' ? <PictureAsPdfIcon sx={{ mr: 1, verticalAlign: 'middle' }} /> : <DescriptionIcon sx={{ mr: 1, verticalAlign: 'middle' }} />}
        {fileName || 'Dokument'}
      </DialogTitle>
      <DialogContent sx={{ minHeight: 500 }}>
        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', pt: 10 }}><CircularProgress /></Box>}
        {error && <Alert severity="error">{error}</Alert>}
        {blobUrl && type === 'pdf' && (
          <iframe
            src={blobUrl}
            title="PDF-visning"
            style={{ width: '100%', height: '70vh', border: 'none' }}
          />
        )}
        {blobUrl && type === 'word' && isImageDoc && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Box
              component="img"
              src={blobUrl}
              alt={fileName || 'Bild'}
              sx={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
            />
          </Box>
        )}
        {blobUrl && type === 'word' && !isImageDoc && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" gutterBottom>
              Word-filer kan inte förhandsgranskas direkt i webbläsaren.
            </Typography>
            <Button
              variant="contained"
              href={blobUrl}
              download={fileName || 'document.docx'}
              startIcon={<DownloadIcon />}
            >
              Ladda ner Word-fil
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Stäng</Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Batch helpers
// ---------------------------------------------------------------------------
function extractArtNrFromFilename(filename: string): string | null {
  const match = filename.match(/^(\d+)/);
  return match ? match[1] : null;
}

interface BatchGroup {
  artikelnummer: string;
  labelFiles: File[];
  supplierFile: File | null;
}

function groupBatchFiles(labelFiles: File[], supplierFiles: File[]): BatchGroup[] {
  const groups = new Map<string, BatchGroup>();
  for (const f of labelFiles) {
    const art = extractArtNrFromFilename(f.name);
    if (!art) continue;
    if (!groups.has(art)) groups.set(art, { artikelnummer: art, labelFiles: [], supplierFile: null });
    groups.get(art)!.labelFiles.push(f);
  }
  for (const f of supplierFiles) {
    const art = extractArtNrFromFilename(f.name);
    if (!art) continue;
    if (!groups.has(art)) groups.set(art, { artikelnummer: art, labelFiles: [], supplierFile: null });
    groups.get(art)!.supplierFile = f;
  }
  return Array.from(groups.values()).sort((a, b) => a.artikelnummer.localeCompare(b.artikelnummer));
}

// ---------------------------------------------------------------------------
// BatchResultCard — collapsible result card for one product in batch view
// ---------------------------------------------------------------------------
function BatchResultCard({ result, index }: { result: ComparisonResult; index: number }) {
  const [expanded, setExpanded] = useState(false);
  const matchCount = result.fields.filter(f => f.status === 'match').length;
  const mismatchCount = result.fields.filter(f => f.status === 'mismatch').length;

  return (
    <Paper
      variant="outlined"
      sx={{
        mb: 2,
        borderRadius: '12px',
        borderColor: PC_COLORS.border,
        bgcolor: PC_COLORS.surface,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 2.25, py: 1.75, cursor: 'pointer', display: 'flex',
          justifyContent: 'space-between', alignItems: 'center',
          '&:hover': { bgcolor: PC_COLORS.surface2 },
        }}
        onClick={() => setExpanded(v => !v)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography className="pc-mono" sx={{ minWidth: 24, fontSize: '0.76rem', color: PC_COLORS.textMuted }}>{index + 1}.</Typography>
          <Box>
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: PC_COLORS.textPrimary }}>{result.produktnamn || result.artikelnummer}</Typography>
            <Typography sx={{ fontSize: '0.76rem', color: PC_COLORS.textSecondary }}>
              {result.artikelnummer}{result.assetId ? '' : ' — Ej hittad i DAM'} • {formatAiProvider(result.aiProvider, result.aiModel)}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {result.fields.length > 0 && (
            <>
              <Box sx={{ px: 1.1, py: 0.45, borderRadius: '999px', border: '1px solid', borderColor: PC_COLORS.okBorder, bgcolor: PC_COLORS.okBg, color: PC_COLORS.ok, fontSize: '0.72rem', fontWeight: 600 }}>
                {matchCount} match
              </Box>
              <Box sx={{ px: 1.1, py: 0.45, borderRadius: '999px', border: '1px solid', borderColor: mismatchCount > 0 ? PC_COLORS.critBorder : PC_COLORS.border, bgcolor: mismatchCount > 0 ? PC_COLORS.critBg : PC_COLORS.surface2, color: mismatchCount > 0 ? PC_COLORS.crit : PC_COLORS.textSecondary, fontSize: '0.72rem', fontWeight: 600 }}>
                {mismatchCount} avvikelse{mismatchCount === 1 ? '' : 'r'}
              </Box>
            </>
          )}
          <StatusBadge status={result.overallStatus === 'ok' ? 'match' : result.overallStatus === 'has_regulatory_warnings' ? 'mismatch' : result.fields.length === 0 ? 'missing' : 'mismatch'} />
          <Box sx={{ color: PC_COLORS.textMuted, fontSize: '0.72rem', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>▼</Box>
        </Box>
      </Box>
      <Collapse in={expanded}>
        <Divider sx={{ borderColor: PC_COLORS.border }} />
        <Box sx={{ p: 2.25, bgcolor: '#FAFAF8' }}>
          {result.regulatoryWarnings.length > 0 && (
            <Alert severity="error" sx={{ mb: 2, borderColor: PC_COLORS.critBorder, bgcolor: PC_COLORS.critBg }} variant="outlined">
              {result.regulatoryWarnings.map((w, i) => (
                <Typography key={i} variant="body2">- {w}</Typography>
              ))}
            </Alert>
          )}
          {result.fields.length > 0
            ? <ComparisonTable fields={result.fields} />
            : <Typography color="text.secondary" variant="body2">Inga fält att visa.</Typography>
          }
          {(result.rawLabelText || result.rawSupplierText) && (
            <RawTextSection rawLabelText={result.rawLabelText} rawSupplierText={result.rawSupplierText} />
          )}
        </Box>
      </Collapse>
    </Paper>
  );
}

function V2RunResultCard({ result, index }: { result: ComparisonResult; index: number }) {
  const [expanded, setExpanded] = useState(false);
  if (!result.v2Result) return null;

  return (
    <Paper
      variant="outlined"
      sx={{
        mb: 2,
        borderRadius: '12px',
        borderColor: PC_COLORS.border,
        bgcolor: PC_COLORS.surface,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 2.25, py: 1.75, cursor: 'pointer', display: 'flex',
          justifyContent: 'space-between', alignItems: 'center',
          '&:hover': { bgcolor: PC_COLORS.surface2 },
        }}
        onClick={() => setExpanded(v => !v)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography className="pc-mono" sx={{ minWidth: 24, fontSize: '0.76rem', color: PC_COLORS.textMuted }}>{index + 1}.</Typography>
          <Box>
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: PC_COLORS.textPrimary }}>{result.produktnamn || result.artikelnummer}</Typography>
            <Typography sx={{ fontSize: '0.76rem', color: PC_COLORS.textSecondary }}>
              {result.artikelnummer} • {formatAiProvider(result.aiProvider, result.aiModel)}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <ComparisonVersionBadge version="v2" />
          <OverallStatusBadge status={result.overallStatus} />
          <Box sx={{ color: PC_COLORS.textMuted, fontSize: '0.72rem', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>▼</Box>
        </Box>
      </Box>
      <Collapse in={expanded}>
        <Divider sx={{ borderColor: PC_COLORS.border }} />
        <Box sx={{ p: 2.25, bgcolor: '#FAFAF8' }}>
          {result.regulatoryWarnings.length > 0 && (
            <Alert severity="error" sx={{ mb: 2, borderColor: PC_COLORS.critBorder, bgcolor: PC_COLORS.critBg }} variant="outlined">
              {result.regulatoryWarnings.map((warning, warningIndex) => (
                <Typography key={warningIndex} variant="body2">- {warning}</Typography>
              ))}
            </Alert>
          )}
          <V2ScoreBar v2Result={result.v2Result} />
          <V2ComparisonView v2Result={result.v2Result} />
          {(result.rawLabelText || result.rawSupplierText) && (
            <RawTextSection rawLabelText={result.rawLabelText} rawSupplierText={result.rawSupplierText} />
          )}
        </Box>
      </Collapse>
    </Paper>
  );
}

function createTestRunGroupId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return `pc_test_${crypto.randomUUID()}`;
    }
  } catch { /* ignore */ }
  return `pc_test_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function summarizeValue(value: string | null | undefined): string {
  const normalized = (value || '').replace(/\s+/g, ' ').trim();
  return normalized || '—';
}

function normalizeSummaryValueForComparison(value: string | null | undefined): string {
  return summarizeValue(value)
    .replace(/\*\*/g, '')
    .replace(/[‐‑‒–—−]/g, '-')
    .replace(/,\s*-\s*(varav)/gi, ', $1')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function formatSummaryFieldStatus(status: ComparisonField['status'] | null | undefined): string {
  switch (status) {
    case 'match':
      return 'match';
    case 'mismatch':
      return 'avvikelse';
    case 'only_one_source':
      return 'bara en källa';
    default:
      return 'saknas';
  }
}

function buildTestRunSummary(results: ComparisonResult[]): string {
  if (results.length === 0) return '';

  const first = results[0];
  const fieldKeys = Array.from(new Set(results.flatMap(run => run.fields.map(field => field.fieldKey))));
  const varyingLines: string[] = [];
  const consistentLines: string[] = [];

  for (const fieldKey of fieldKeys) {
    const perRun = results.map(run => run.fields.find(field => field.fieldKey === fieldKey) || null);
    const label = perRun.find(Boolean)?.label || fieldKey;
    const representativeField = perRun.find(Boolean) || null;
    const signatures = new Set(perRun.map(field => JSON.stringify({
      status: field?.status ?? null,
      damValue: normalizeSummaryValueForComparison(field?.damValue),
      pdfValue: normalizeSummaryValueForComparison(field?.pdfValue),
      wordValue: normalizeSummaryValueForComparison(field?.wordValue),
    })));

    if (signatures.size > 1) {
      varyingLines.push(`- ${label}`);
      perRun.forEach((field, index) => {
        varyingLines.push(
          `  Körning ${index + 1}: status=${formatSummaryFieldStatus(field?.status)} | DABAS=${summarizeValue(field?.damValue)} | etikett=${summarizeValue(field?.pdfValue)} | leverantör=${summarizeValue(field?.wordValue)}`,
        );
      });
    } else {
      consistentLines.push(
        `- ${label}: status=${formatSummaryFieldStatus(representativeField?.status)} | DABAS=${summarizeValue(representativeField?.damValue)} | etikett=${summarizeValue(representativeField?.pdfValue)} | leverantör=${summarizeValue(representativeField?.wordValue)}`,
      );
    }
  }

  const lines = [
    `Testkörning x${results.length}`,
    `Artikelnummer: ${first.artikelnummer}`,
    `Produkt: ${first.produktnamn}`,
    `AI: ${formatAiProvider(first.aiProvider, first.aiModel)}`,
  ];

  if (first.testRunGroupId) {
    lines.push(`Testgrupp: ${first.testRunGroupId}`);
  }

  lines.push('');

  results.forEach((run, index) => {
    lines.push(`Körning ${index + 1}: ${formatOverallStatus(run.overallStatus)}`);
    if (run.regulatoryWarnings.length > 0) {
      lines.push(`Varningar ${index + 1}: ${run.regulatoryWarnings.join(' | ')}`);
    }
  });

  lines.push('');
  lines.push(varyingLines.length > 0 ? 'Fält som varierade mellan körningarna:' : 'Fält som varierade mellan körningarna: inga');
  if (varyingLines.length > 0) {
    lines.push(...varyingLines);
  }

  lines.push('');
  lines.push('Obs: "konsekvent" betyder samma utfall i alla tre körningar, inte att fältet automatiskt är korrekt mot DABAS, etikett eller leverantörsdokument.');
  lines.push('');
  lines.push(consistentLines.length > 0 ? 'Konsekventa fält mellan körningarna:' : 'Konsekventa fält mellan körningarna: inga');
  if (consistentLines.length > 0) {
    lines.push(...consistentLines);
  }

  return lines.join('\n');
}

function normalizeV2SummaryValueForComparison(value: string | null | undefined): string {
  return summarizeValue(value)
    .replace(/[‐‑‒–—−]/g, '-')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function buildV2TestRunSummary(results: ComparisonResult[]): string {
  if (results.length === 0) return '';
  const first = results[0];
  const firstV2 = first.v2Result;
  if (!firstV2) return '';

  const sectionDefinitions = [
    { section: 'ingredients' as const, label: 'Ingredienser' },
    { section: 'nutritionPer100g' as const, label: 'Näringsvärde per 100 g' },
    { section: 'allergens' as const, label: 'Allergener' },
    { section: 'productInfo' as const, label: 'Övrig produktinformation' },
  ];

  const varyingLines: string[] = [];
  const consistentLines: string[] = [];

  for (const section of sectionDefinitions) {
    const rowMap = new Map<string, V2ComparisonRow>();
    for (const run of results) {
      const sectionRows = run.v2Result?.sections[section.section] || [];
      sectionRows.forEach((row) => {
        if (!rowMap.has(row.key)) {
          rowMap.set(row.key, row);
        }
      });
    }

    const rows = Array.from(rowMap.values()).sort((a, b) => {
      if (section.section !== 'ingredients') return 0;
      const getIndex = (key: string) => Number(key.replace('ingredient_', '')) || 0;
      return getIndex(a.key) - getIndex(b.key);
    });

    for (const row of rows) {
      const perRun = results.map((run) => {
        const v2 = run.v2Result;
        if (!v2) return null;
        const sectionRows = v2.sections[section.section as keyof typeof v2.sections] as V2ComparisonRow[];
        return sectionRows.find((candidate) => candidate.key === row.key) || null;
      });

      const signatures = new Set(perRun.map((candidate) => JSON.stringify({
        status: candidate?.status ?? null,
        dabas: normalizeV2SummaryValueForComparison(candidate?.dabas),
        supplier: normalizeV2SummaryValueForComparison(candidate?.supplier),
        labelDoc: normalizeV2SummaryValueForComparison(candidate?.labelDoc),
        note: normalizeV2SummaryValueForComparison(candidate?.note),
      })));

      const title = `${section.label} — ${row.label}`;
      if (signatures.size > 1) {
        varyingLines.push(`- ${title}`);
        perRun.forEach((candidate, index) => {
          varyingLines.push(
            `  Körning ${index + 1}: status=${candidate?.status || 'missing'} | DABAS=${summarizeValue(candidate?.dabas)} | etikett=${summarizeValue(candidate?.labelDoc)} | leverantör=${summarizeValue(candidate?.supplier)} | notering=${summarizeValue(candidate?.note)}`,
          );
        });
      } else {
        const candidate = perRun.find(Boolean);
        consistentLines.push(
          `- ${title}: status=${candidate?.status || 'missing'} | DABAS=${summarizeValue(candidate?.dabas)} | etikett=${summarizeValue(candidate?.labelDoc)} | leverantör=${summarizeValue(candidate?.supplier)} | notering=${summarizeValue(candidate?.note)}`,
        );
      }
    }
  }

  const lines = [
    `Testkörning x${results.length} v2`,
    `Artikelnummer: ${first.artikelnummer}`,
    `Produkt: ${first.produktnamn}`,
    `AI: ${formatAiProvider(first.aiProvider, first.aiModel)}`,
  ];

  if (first.testRunGroupId) {
    lines.push(`Testgrupp: ${first.testRunGroupId}`);
  }

  lines.push('');
  results.forEach((run, index) => {
    lines.push(`Körning ${index + 1}: ${formatOverallStatus(run.overallStatus)}`);
    if (run.regulatoryWarnings.length > 0) {
      lines.push(`Varningar ${index + 1}: ${run.regulatoryWarnings.join(' | ')}`);
    }
  });

  lines.push('');
  lines.push(varyingLines.length > 0 ? 'Rader som varierade mellan körningarna:' : 'Rader som varierade mellan körningarna: inga');
  if (varyingLines.length > 0) {
    lines.push(...varyingLines);
  }

  lines.push('');
  lines.push('Obs: "konsekvent" betyder samma utfall i alla tre körningar, inte att raderna automatiskt är korrekta mot källorna.');
  lines.push('');
  lines.push(consistentLines.length > 0 ? 'Konsekventa rader mellan körningarna:' : 'Konsekventa rader mellan körningarna: inga');
  if (consistentLines.length > 0) {
    lines.push(...consistentLines);
  }

  return lines.join('\n');
}

function getRequestErrorMessage(err: any, fallback: string): string {
  return err?.response?.data?.message || err?.message || fallback;
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------
const ProductComparisonPage: React.FC = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // Upload state (single tab)
  const [artikelnummer, setArtikelnummer] = useState('');
  const [searchOptions, setSearchOptions] = useState<AssetSearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [pdfFiles, setPdfFiles] = useState<File[]>([]);
  const [showSinglePreviews, setShowSinglePreviews] = useState(false);
  const [singleCropBottomPctByFile, setSingleCropBottomPctByFile] = useState<Record<string, number>>({});
  const [singlePdfCropSelectionByFile, setSinglePdfCropSelectionByFile] = useState<Record<string, LabelPdfCropSelection | null>>({});
  const [singlePdfCropDialogKey, setSinglePdfCropDialogKey] = useState<string | null>(null);
  const [singlePdfCropQueue, setSinglePdfCropQueue] = useState<string[]>([]);
  const [supplierFile, setSupplierFile] = useState<File | null>(null);
  const [aiProvider, setAiProvider] = useState<AiProvider>('openai');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testRunResults, setTestRunResults] = useState<ComparisonResult[]>([]);
  const [testRunSummary, setTestRunSummary] = useState('');
  const [testRunGroupId, setTestRunGroupId] = useState<string | null>(null);
  const [testProgress, setTestProgress] = useState<{ current: number; total: number } | null>(null);

  // Batch state
  const [batchLabelFiles, setBatchLabelFiles] = useState<File[]>([]);
  const [showBatchPreviews, setShowBatchPreviews] = useState(false);
  const [batchCropBottomPctByFile, setBatchCropBottomPctByFile] = useState<Record<string, number>>({});
  const [batchSupplierFiles, setBatchSupplierFiles] = useState<File[]>([]);
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchError, setBatchError] = useState<string | null>(null);
  const [batchResults, setBatchResults] = useState<ComparisonResult[]>([]);
  const [batchProgress, setBatchProgress] = useState<{ current: number; total: number; art: string } | null>(null);

  // Result state
  const [result, setResult] = useState<ComparisonResult | null>(null);

  // History state
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<ComparisonHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [historyMode, setHistoryMode] = useState<HistoryMode>('standard');
  const [historyQuery, setHistoryQuery] = useState('');
  const [selectedHistoryIds, setSelectedHistoryIds] = useState<Set<string>>(new Set());
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());

  // Detail view state (when viewing from history)
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detail, setDetail] = useState<ComparisonDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Document viewer
  const [docViewer, setDocViewer] = useState<{ comparisonId: string; type: 'pdf' | 'word'; fileName: string | null; pdfIndex?: number | null } | null>(null);

  // Auto-detect article number from uploaded filenames (single tab)
  useEffect(() => {
    if (artikelnummer.trim()) return;
    const firstFile = pdfFiles[0] || supplierFile;
    if (!firstFile) return;
    const detected = extractArtNrFromFilename(firstFile.name);
    if (detected) setArtikelnummer(detected);
  }, [pdfFiles, supplierFile, artikelnummer]);

  // Keep single crop settings in sync with selected label files
  useEffect(() => {
    setSingleCropBottomPctByFile(prev => {
      const next: Record<string, number> = {};
      for (const f of pdfFiles) {
        const key = fileStableKey(f);
        next[key] = prev[key] ?? 0;
      }
      return next;
    });
  }, [pdfFiles]);

  useEffect(() => {
    setSinglePdfCropSelectionByFile(prev => {
      const next: Record<string, LabelPdfCropSelection | null> = {};
      for (const file of pdfFiles) {
        if (!isPdfFile(file)) continue;
        const key = fileStableKey(file);
        next[key] = prev[key] ?? null;
      }
      return next;
    });
  }, [pdfFiles]);

  const previousSinglePdfKeysRef = useRef<string[]>([]);

  useEffect(() => {
    const currentPdfKeys = pdfFiles.filter(isPdfFile).map(fileStableKey);
    const currentPdfKeySet = new Set(currentPdfKeys);
    const previousKeySet = new Set(previousSinglePdfKeysRef.current);
    const newKeys = currentPdfKeys.filter((key) => !previousKeySet.has(key));

    if (newKeys.length > 0) {
      setShowSinglePreviews(true);
      setSinglePdfCropQueue((prev) => [
        ...prev.filter((key) => currentPdfKeySet.has(key)),
        ...newKeys.filter((key) => !prev.includes(key)),
      ]);
    } else {
      setSinglePdfCropQueue((prev) => prev.filter((key) => currentPdfKeySet.has(key)));
    }

    if (singlePdfCropDialogKey && !currentPdfKeySet.has(singlePdfCropDialogKey)) {
      setSinglePdfCropDialogKey(null);
    }

    previousSinglePdfKeysRef.current = currentPdfKeys;
  }, [pdfFiles, singlePdfCropDialogKey]);

  useEffect(() => {
    if (singlePdfCropDialogKey || singlePdfCropQueue.length === 0) {
      return;
    }

    const [nextKey, ...rest] = singlePdfCropQueue;
    setSinglePdfCropDialogKey(nextKey);
    setSinglePdfCropQueue(rest);
  }, [singlePdfCropDialogKey, singlePdfCropQueue]);

  // Keep batch crop settings in sync with selected batch label files
  useEffect(() => {
    setBatchCropBottomPctByFile(prev => {
      const next: Record<string, number> = {};
      for (const f of batchLabelFiles) {
        const key = fileStableKey(f);
        next[key] = prev[key] ?? 0;
      }
      return next;
    });
  }, [batchLabelFiles]);

  // Search debounce
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleSearchInput = useCallback((value: string) => {
    setArtikelnummer(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (value.length < 2) { setSearchOptions([]); return; }
    searchTimerRef.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const results = await searchAssets(value);
        setSearchOptions(results);
      } catch { /* ignore */ }
      setSearchLoading(false);
    }, 300);
  }, []);

  const handleSupplierFileChange = useCallback((file: File | null) => {
    if (!file) {
      setSupplierFile(null);
      return;
    }
    if (isLegacyDocFile(file)) {
      setError(getLegacyDocUploadMessage([file.name]));
      return;
    }
    setError(null);
    setSupplierFile(file);
  }, []);

  const handleBatchSupplierFilesChange = useCallback((files: File[]) => {
    const invalidFiles = files.filter(isLegacyDocFile);
    const validFiles = files.filter((file) => !isLegacyDocFile(file));
    if (invalidFiles.length > 0) {
      setBatchError(getLegacyDocUploadMessage(invalidFiles.map((file) => file.name)));
    } else {
      setBatchError(null);
    }
    setBatchSupplierFiles(validFiles);
  }, []);

  const handleOpenResultDocument = useCallback((
    comparisonId: string | undefined,
    type: 'pdf' | 'word',
    fileName: string | null,
    pdfIndex?: number | null,
  ) => {
    if (!comparisonId) {
      setError('Dokumentet kan inte öppnas för denna körning ännu. Kör jämförelsen igen om problemet kvarstår.');
      return;
    }
    setDocViewer({ comparisonId, type, fileName, pdfIndex: type === 'pdf' ? pdfIndex ?? null : null });
  }, []);

  // Run comparison
  const handleCompare = useCallback(async () => {
    if (!artikelnummer.trim()) return;
    // Strip product name if user selected from autocomplete (e.g. "97034 — Feta Originalet" → "97034")
    const cleanArtNr = artikelnummer.includes(' — ') ? artikelnummer.split(' — ')[0].trim() : artikelnummer.trim();
    const labelCropBottomPctList = pdfFiles.map(f => singleCropBottomPctByFile[fileStableKey(f)] ?? 0);
    const labelPdfCropSelections = buildLabelPdfCropSelectionList(pdfFiles, singlePdfCropSelectionByFile);
    setLoading(true);
    setError(null);
    setResult(null);
    setTestRunResults([]);
    setTestRunSummary('');
    setTestRunGroupId(null);
    setTestProgress(null);
    try {
      const res = await extractAndCompare(cleanArtNr, pdfFiles, supplierFile, aiProvider, {
        labelCropBottomPctList,
        labelPdfCropSelections,
      });
      setResult(res);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Ett fel uppstod.');
    }
    setLoading(false);
  }, [artikelnummer, pdfFiles, supplierFile, aiProvider, singleCropBottomPctByFile, singlePdfCropSelectionByFile]);

  const handleTestRun = useCallback(async () => {
    if (!artikelnummer.trim()) return;
    const cleanArtNr = artikelnummer.includes(' — ') ? artikelnummer.split(' — ')[0].trim() : artikelnummer.trim();
    const labelCropBottomPctList = pdfFiles.map(f => singleCropBottomPctByFile[fileStableKey(f)] ?? 0);
    const labelPdfCropSelections = buildLabelPdfCropSelectionList(pdfFiles, singlePdfCropSelectionByFile);
    const totalRuns = 3;
    const groupId = createTestRunGroupId();

    setLoading(true);
    setError(null);
    setResult(null);
    setTestRunResults([]);
    setTestRunSummary('');
    setTestRunGroupId(groupId);
    setTestProgress({ current: 1, total: totalRuns });

    const runs: ComparisonResult[] = [];

    try {
      for (let runIndex = 1; runIndex <= totalRuns; runIndex += 1) {
        setTestProgress({ current: runIndex, total: totalRuns });
        const res = await extractAndCompare(cleanArtNr, pdfFiles, supplierFile, aiProvider, {
          labelCropBottomPctList,
          labelPdfCropSelections,
          isTestRun: true,
          testRunGroupId: groupId,
          testRunIndex: runIndex,
          testRunTotal: totalRuns,
        });
        runs.push(res);
        setTestRunResults([...runs]);
      }

      setTestRunSummary(buildTestRunSummary(runs));
    } catch (err: any) {
      setError(getRequestErrorMessage(err, 'Kunde inte köra testkörningen.'));
    }

    setLoading(false);
    setTestProgress(null);
  }, [artikelnummer, pdfFiles, supplierFile, aiProvider, singleCropBottomPctByFile, singlePdfCropSelectionByFile]);

  const handleCompareV2 = useCallback(async () => {
    if (!artikelnummer.trim()) return;
    const cleanArtNr = artikelnummer.includes(' — ') ? artikelnummer.split(' — ')[0].trim() : artikelnummer.trim();
    const labelPdfCropSelections = buildLabelPdfCropSelectionList(pdfFiles, singlePdfCropSelectionByFile);
    setLoading(true);
    setError(null);
    setResult(null);
    setTestRunResults([]);
    setTestRunSummary('');
    setTestRunGroupId(null);
    setTestProgress(null);
    try {
      const res = await extractAndCompareV2(cleanArtNr, pdfFiles, supplierFile, aiProvider, {
        labelPdfCropSelections,
      });
      setResult(res);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Ett fel uppstod.');
    }
    setLoading(false);
  }, [artikelnummer, pdfFiles, supplierFile, aiProvider, singlePdfCropSelectionByFile]);

  const handleTestRunV2 = useCallback(async () => {
    if (!artikelnummer.trim()) return;
    const cleanArtNr = artikelnummer.includes(' — ') ? artikelnummer.split(' — ')[0].trim() : artikelnummer.trim();
    const labelPdfCropSelections = buildLabelPdfCropSelectionList(pdfFiles, singlePdfCropSelectionByFile);
    const totalRuns = 3;
    const groupId = createTestRunGroupId();

    setLoading(true);
    setError(null);
    setResult(null);
    setTestRunResults([]);
    setTestRunSummary('');
    setTestRunGroupId(groupId);
    setTestProgress({ current: 1, total: totalRuns });

    const runs: ComparisonResult[] = [];

    try {
      for (let runIndex = 1; runIndex <= totalRuns; runIndex += 1) {
        setTestProgress({ current: runIndex, total: totalRuns });
        const res = await extractAndCompareV2(cleanArtNr, pdfFiles, supplierFile, aiProvider, {
          labelPdfCropSelections,
          isTestRun: true,
          testRunGroupId: groupId,
          testRunIndex: runIndex,
          testRunTotal: totalRuns,
        });
        runs.push(res);
        setTestRunResults([...runs]);
      }

      setTestRunSummary(buildV2TestRunSummary(runs));
    } catch (err: any) {
      setError(getRequestErrorMessage(err, 'Kunde inte köra testkörningen v2.'));
    }

    setLoading(false);
    setTestProgress(null);
  }, [artikelnummer, pdfFiles, supplierFile, aiProvider, singlePdfCropSelectionByFile]);

  // Load history
  const loadHistory = useCallback(async (
    page = 1,
    mode: HistoryMode = historyMode,
    query: string = historyQuery,
  ) => {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const res = await getHistory(page, 20, mode, query.trim());
      setHistory(res.comparisons);
      setHistoryTotal(res.total);
      setHistoryPage(page);
      setHistoryMode(mode);
    } catch (err: any) {
      setHistoryError(getRequestErrorMessage(err, 'Kunde inte hämta dina körningar.'));
    }
    setHistoryLoading(false);
  }, [historyMode, historyQuery]);

  const handleApplyHistorySearch = useCallback(() => {
    setSelectedHistoryIds(new Set());
    loadHistory(1, historyMode, historyQuery);
  }, [historyMode, historyQuery, loadHistory]);

  // Load detail
  const loadDetail = useCallback(async (id: string) => {
    setDetailLoading(true);
    setHistoryError(null);
    setDetailId(id);
    try {
      const res = await getComparison(id);
      setDetail(res);
    } catch (err: any) {
      setDetailId(null);
      setDetail(null);
      setHistoryError(getRequestErrorMessage(err, 'Kunde inte öppna den valda körningen.'));
    }
    setDetailLoading(false);
  }, []);

  // Resolve field in detail view
  const handleResolveField = useCallback(async (fieldKey: string) => {
    if (!detailId || !detail) return;
    setHistoryError(null);
    try {
      const res = await resolveFields(detailId, [fieldKey]);
      setDetail(prev => prev ? { ...prev, resolvedFields: res.resolvedFields } : prev);
    } catch (err: any) {
      setHistoryError(getRequestErrorMessage(err, 'Kunde inte spara ändringen.'));
    }
  }, [detailId, detail]);

  // Export
  const handleExport = useCallback(async (id: string) => {
    setHistoryError(null);
    try {
      const res = await axiosInstance.get(getExportUrl(id), { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `produktkontroll_${id}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setHistoryError(getRequestErrorMessage(err, 'Kunde inte exportera körningen.'));
    }
  }, []);

  // Delete single history item
  const handleDeleteOne = useCallback(async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('Radera denna jämförelse?')) return;
    setHistoryError(null);
    setDeletingIds(prev => new Set(prev).add(id));
    try {
      await deleteComparison(id);
      setHistory(prev => prev.filter(h => h._id !== id));
      setHistoryTotal(prev => prev - 1);
      setSelectedHistoryIds(prev => { const s = new Set(prev); s.delete(id); return s; });
    } catch (err: any) {
      setHistoryError(getRequestErrorMessage(err, 'Kunde inte radera körningen.'));
    }
    setDeletingIds(prev => { const s = new Set(prev); s.delete(id); return s; });
  }, []);

  // Delete selected history items
  const handleDeleteSelected = useCallback(async () => {
    const ids = Array.from(selectedHistoryIds);
    if (ids.length === 0) return;
    if (!window.confirm(`Radera ${ids.length} valda jämförelse(r)?`)) return;
    setHistoryError(null);
    try {
      const res = await deleteComparisons(ids);
      setSelectedHistoryIds(new Set());
      if (res.deleted < ids.length) {
        setHistoryError(`${res.deleted} av ${ids.length} valda körningar raderades.`);
      }
      await loadHistory(historyPage);
    } catch (err: any) {
      setHistoryError(getRequestErrorMessage(err, 'Kunde inte radera valda körningar.'));
    }
  }, [selectedHistoryIds, loadHistory, historyPage]);

  // Reset (single tab)
  const handleReset = () => {
    setResult(null);
    setError(null);
    setTestRunResults([]);
    setTestRunSummary('');
    setTestRunGroupId(null);
    setTestProgress(null);
    setPdfFiles([]);
    setShowSinglePreviews(false);
    setSingleCropBottomPctByFile({});
    setSinglePdfCropSelectionByFile({});
    setSinglePdfCropDialogKey(null);
    setSinglePdfCropQueue([]);
    setSupplierFile(null);
    setArtikelnummer('');
    setSearchOptions([]);
    setDetailId(null);
    setDetail(null);
  };

  const activeSinglePdfCropFile = singlePdfCropDialogKey
    ? pdfFiles.find((file) => isPdfFile(file) && fileStableKey(file) === singlePdfCropDialogKey) || null
    : null;

  // Batch run — processes each detected group sequentially
  const handleBatchRun = useCallback(async () => {
    const groups = groupBatchFiles(batchLabelFiles, batchSupplierFiles);
    if (groups.length === 0) return;
    setBatchLoading(true);
    setBatchError(null);
    setBatchResults([]);
    const results: ComparisonResult[] = [];
    for (let i = 0; i < groups.length; i++) {
      const g = groups[i];
      setBatchProgress({ current: i + 1, total: groups.length, art: g.artikelnummer });
      try {
        const cropBottomPctList = g.labelFiles.map(f => batchCropBottomPctByFile[fileStableKey(f)] ?? 0);
        const res = await extractAndCompare(g.artikelnummer, g.labelFiles, g.supplierFile, aiProvider, {
          labelCropBottomPctList: cropBottomPctList,
        });
        results.push(res);
      } catch (err: any) {
        results.push({
          artikelnummer: g.artikelnummer,
          produktnamn: g.artikelnummer,
          assetId: null,
          aiProvider,
          aiModel: aiProvider === 'openai' ? 'gpt-5.4' : 'claude',
          fields: [],
          overallStatus: 'has_mismatches',
          regulatoryWarnings: [`Fel: ${err?.response?.data?.message || err?.message || 'Okänt fel'}`],
          pdfFileName: g.labelFiles[0]?.name || null,
          wordFileName: g.supplierFile?.name || null,
          extractedAt: new Date().toISOString(),
        });
      }
      setBatchResults([...results]);
    }
    setBatchLoading(false);
    setBatchProgress(null);
  }, [batchLabelFiles, batchSupplierFiles, batchCropBottomPctByFile, aiProvider]);

  // ---------------------------------------------------------------------------
  // Render: Detail view (from history)
  // ---------------------------------------------------------------------------
  if (detailId && detail) {
    const detailVersion = getComparisonVersion(detail);
    return (
      <Box className="product-comparison-page" sx={{ bgcolor: PC_COLORS.bg, p: { xs: 2, md: 3 }, borderRadius: '16px' }}>
        <Box sx={{ mb: 1.5 }}>
          <Typography sx={{ fontSize: { xs: '1.55rem', md: '2rem' }, fontWeight: 600, color: PC_COLORS.textPrimary, lineHeight: 1.05 }}>
            {detail.produktnamn}
          </Typography>
        </Box>
        <Box sx={STICKY_HEADER_SX}>
          <Box sx={{ pt: 0.25 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2.5, gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ mt: 0.5, fontSize: '0.82rem', color: PC_COLORS.textSecondary }}>
                  Artikelnummer: {detail.artikelnummer}
                  {detail.assetId ? ' • Hittad i DAM' : ' • Ej hittad i DAM'}
                  {` • ${new Date(detail.createdAt).toLocaleString('sv-SE')}`}
                  {` • AI: ${formatAiProvider(detail.aiProvider, detail.aiModel)}`}
                  {detail.isTestRun ? ` • Test ${detail.testRunIndex || '?'} / ${detail.testRunTotal || '?'}` : ''}
                </Typography>
              </Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={() => handleExport(detailId!)} sx={{ borderColor: PC_COLORS.borderStrong, color: PC_COLORS.textPrimary, bgcolor: PC_COLORS.surface, textTransform: 'none' }}>
                  Exportera CSV
                </Button>
                <Button size="small" variant="outlined" onClick={() => { setHistoryError(null); setDetailId(null); setDetail(null); }} sx={{ borderColor: PC_COLORS.borderStrong, color: PC_COLORS.textPrimary, bgcolor: PC_COLORS.surface, textTransform: 'none' }}>
                  Tillbaka
                </Button>
              </Stack>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <ComparisonVersionBadge version={detailVersion} />
              <OverallStatusBadge status={detail.overallStatus} />
              <Chip label={`AI: ${formatAiProvider(detail.aiProvider, detail.aiModel)}`} size="small" variant="outlined" sx={{ borderColor: PC_COLORS.borderStrong, bgcolor: PC_COLORS.surface }} />
                {getCroppedPdfChipLabel(detail) && (
                  <Tooltip title={getCroppedLabelAiInputs(detail).map((input) => input.aiFileName || 'Beskuren PDF').join(', ')}>
                    <Chip
                      label={getCroppedPdfChipLabel(detail)}
                      size="small"
                      color="success"
                      icon={<CropFreeIcon />}
                      sx={{ bgcolor: PC_COLORS.okBg, borderColor: PC_COLORS.okBorder }}
                    />
                  </Tooltip>
                )}
                {getPdfDocumentEntries(detail).map((entry) => (
                  <Chip
                    key={`${detail._id}-pdf-${entry.pdfIndex ?? 'legacy'}-${entry.fileName}`}
                    label={`PDF: ${entry.fileName}`}
                    size="small"
                    variant="outlined"
                    icon={<PictureAsPdfIcon />}
                    onClick={() => handleOpenResultDocument(detail._id, 'pdf', entry.fileName, entry.pdfIndex)}
                    sx={{ borderColor: PC_COLORS.borderStrong, bgcolor: PC_COLORS.surface, cursor: 'pointer' }}
                  />
                ))}
              {detail.wordFileName && (
                <Chip
                  label={`Leverantör: ${detail.wordFileName}`}
                  size="small"
                  variant="outlined"
                  icon={<DescriptionIcon />}
                  onClick={() => handleOpenResultDocument(detail._id, 'word', detail.wordFileName)}
                  sx={{ borderColor: PC_COLORS.borderStrong, bgcolor: PC_COLORS.surface, cursor: 'pointer' }}
                />
              )}
            </Box>

            <CriticalWarningBanner warnings={detail.regulatoryWarnings} />
            {isV2Comparison(detail) && (
              <Alert severity="info" sx={{ mb: 2, borderColor: '#BFDBFE', bgcolor: '#EFF6FF' }} variant="outlined">
                Källa in: etikett = {formatV2SourceMode(detail.v2Result.sourceAudit.labelMode)}, leverantör = {formatV2SourceMode(detail.v2Result.sourceAudit.supplierMode)}.
              </Alert>
            )}
            {isV2Comparison(detail)
              ? <V2ScoreBar v2Result={detail.v2Result} />
              : <ComparisonScoreBar fields={detail.fields} />}

            {historyError && <Alert severity="error" sx={{ mt: 2 }}>{historyError}</Alert>}
          </Box>
        </Box>

        {isV2Comparison(detail) ? (
          <V2ComparisonView
            v2Result={detail.v2Result}
            resolvedFields={detail.resolvedFields}
            onResolve={handleResolveField}
            showSourceAuditAlert={false}
          />
        ) : (
          <ComparisonTable
            fields={detail.fields}
            resolvedFields={detail.resolvedFields}
            onResolve={handleResolveField}
          />
        )}

        <RawTextSection rawLabelText={detail.rawLabelText} rawSupplierText={detail.rawSupplierText} />

        {docViewer && (
          <DocumentViewerDialog
            open={!!docViewer}
            onClose={() => setDocViewer(null)}
            comparisonId={docViewer.comparisonId}
            type={docViewer.type}
            fileName={docViewer.fileName}
            pdfIndex={docViewer.pdfIndex}
          />
        )}
      </Box>
    );
  }

  // ---------------------------------------------------------------------------
  // Render: History view
  // ---------------------------------------------------------------------------
  if (showHistory) {
    return (
      <Box className="product-comparison-page" sx={{ bgcolor: PC_COLORS.bg, p: { xs: 2, md: 3 }, borderRadius: '16px' }}>
        <Box sx={STICKY_HEADER_SX}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: { xs: '1.4rem', md: '1.7rem' }, fontWeight: 600, color: PC_COLORS.textPrimary }}>Jämförelsehistorik</Typography>
              <FormControlLabel
                control={(
                  <Switch
                    checked={historyMode === 'test'}
                    onChange={(_e, checked) => {
                      const nextMode: HistoryMode = checked ? 'test' : 'standard';
                      setSelectedHistoryIds(new Set());
                      loadHistory(1, nextMode, historyQuery);
                    }}
                  />
                )}
                label="Visa testresultat"
                sx={{ ml: 0.5, '& .MuiFormControlLabel-label': { fontSize: '0.82rem', color: PC_COLORS.textSecondary } }}
              />
              {historyMode === 'test' && (
                <Chip size="small" label="Endast testresultat" variant="outlined" sx={{ borderColor: PC_COLORS.borderStrong, bgcolor: PC_COLORS.surface }} />
              )}
              {selectedHistoryIds.size > 0 && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DeleteIcon />}
                  onClick={handleDeleteSelected}
                  sx={{ borderColor: PC_COLORS.critBorder, color: PC_COLORS.crit, bgcolor: PC_COLORS.surface, textTransform: 'none' }}
                >
                  Radera valda ({selectedHistoryIds.size})
                </Button>
              )}
            </Box>
            <Stack direction="row" spacing={1}>
              <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={() => loadHistory(historyPage, historyMode, historyQuery)} sx={{ borderColor: PC_COLORS.borderStrong, color: PC_COLORS.textPrimary, bgcolor: PC_COLORS.surface, textTransform: 'none' }}>
                Uppdatera
              </Button>
              <Button size="small" variant="outlined" onClick={() => { setHistoryError(null); setShowHistory(false); setSelectedHistoryIds(new Set()); }} sx={{ borderColor: PC_COLORS.borderStrong, color: PC_COLORS.textPrimary, bgcolor: PC_COLORS.surface, textTransform: 'none' }}>
                Tillbaka
              </Button>
            </Stack>
          </Box>
          <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small"
              value={historyQuery}
              onChange={(e) => setHistoryQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleApplyHistorySearch();
                }
              }}
              placeholder="Sök produktnamn, artikelnummer eller filnamn"
              sx={{
                minWidth: { xs: '100%', sm: 360 },
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  bgcolor: PC_COLORS.surface,
                },
              }}
            />
            <Button size="small" variant="outlined" onClick={handleApplyHistorySearch} sx={{ borderColor: PC_COLORS.borderStrong, color: PC_COLORS.textPrimary, bgcolor: PC_COLORS.surface, textTransform: 'none' }}>
              Sök
            </Button>
            {historyQuery.trim() && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setHistoryQuery('');
                  setSelectedHistoryIds(new Set());
                  loadHistory(1, historyMode, '');
                }}
                sx={{ borderColor: PC_COLORS.borderStrong, color: PC_COLORS.textPrimary, bgcolor: PC_COLORS.surface, textTransform: 'none' }}
              >
                Rensa
              </Button>
            )}
          </Box>
        </Box>
        {(historyLoading || detailLoading) && <LinearProgress sx={{ mb: 2, color: PC_COLORS.accent }} />}
        {historyError && <Alert severity="error" sx={{ mb: 2 }}>{historyError}</Alert>}
        {history.length === 0 && !historyLoading && (
          <ComparisonSurface>
            <Typography sx={{ color: PC_COLORS.textSecondary }}>
              {historyQuery.trim()
                ? 'Ingen historik matchade din sökning.'
                : historyMode === 'test'
                  ? 'Inga testkörningar har gjorts ännu.'
                  : 'Inga egna jämförelser har gjorts ännu.'}
            </Typography>
          </ComparisonSurface>
        )}
        {history.map(item => (
          <Card
            key={item._id}
            variant="outlined"
            sx={{
              mb: 1.25,
              cursor: 'pointer',
              borderRadius: '12px',
              '&:hover': { boxShadow: '0 8px 24px rgba(31, 31, 27, 0.06)' },
              borderColor: selectedHistoryIds.has(item._id) ? PC_COLORS.accent : PC_COLORS.border,
              bgcolor: selectedHistoryIds.has(item._id) ? '#F5F8FF' : PC_COLORS.surface,
            }}
            onClick={() => loadDetail(item._id)}
          >
            <CardContent sx={{ py: 1.75, '&:last-child': { pb: 1.75 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <input
                    type="checkbox"
                    checked={selectedHistoryIds.has(item._id)}
                    onClick={e => e.stopPropagation()}
                    onChange={e => {
                      e.stopPropagation();
                      setSelectedHistoryIds(prev => {
                        const s = new Set(prev);
                        if (e.target.checked) s.add(item._id); else s.delete(item._id);
                        return s;
                      });
                    }}
                  />
                  <Box>
                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: PC_COLORS.textPrimary }}>{item.produktnamn || item.artikelnummer}</Typography>
                    <Typography sx={{ fontSize: '0.76rem', color: PC_COLORS.textSecondary }}>
                      {item.artikelnummer} • {new Date(item.createdAt).toLocaleString('sv-SE')} • {formatAiProvider(item.aiProvider, item.aiModel)}
                      {item.isTestRun ? ` • Test ${item.testRunIndex || '?'} / ${item.testRunTotal || '?'}` : ''}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ComparisonVersionBadge version={getComparisonVersion(item)} />
                  {item.isTestRun && (
                    <Chip
                      label={`Test ${item.testRunIndex || '?'} / ${item.testRunTotal || '?'}`}
                      size="small"
                      variant="outlined"
                      sx={{ borderColor: PC_COLORS.borderStrong, bgcolor: PC_COLORS.surface2 }}
                    />
                  )}
                  {getPdfDocumentEntries(item).map((entry, chipIndex) => (
                    <Tooltip key={`${item._id}-pdf-${entry.pdfIndex ?? 'legacy'}-${entry.fileName}`} title={entry.fileName}>
                      <Chip
                        label={getPdfDocumentEntries(item).length > 1 ? `PDF ${chipIndex + 1}` : 'PDF'}
                        size="small"
                        variant="outlined"
                        icon={<PictureAsPdfIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDocViewer({ comparisonId: item._id, type: 'pdf', fileName: entry.fileName, pdfIndex: entry.pdfIndex });
                        }}
                        sx={{ borderColor: PC_COLORS.border, bgcolor: PC_COLORS.surface2, cursor: 'pointer' }}
                      />
                    </Tooltip>
                  ))}
                  {item.wordFileName && (
                    <Tooltip title={item.wordFileName}>
                      <Chip
                        label="Leverantör"
                        size="small"
                        variant="outlined"
                        icon={<DescriptionIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDocViewer({ comparisonId: item._id, type: 'word', fileName: item.wordFileName });
                        }}
                        sx={{ borderColor: PC_COLORS.border, bgcolor: PC_COLORS.surface2, cursor: 'pointer' }}
                      />
                    </Tooltip>
                  )}
                  <OverallStatusBadge status={item.overallStatus} />
                  <Tooltip title="Radera">
                    <IconButton
                      size="small"
                      disabled={deletingIds.has(item._id)}
                      onClick={e => handleDeleteOne(item._id, e)}
                      sx={{ color: PC_COLORS.crit }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
        {historyTotal > 20 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, gap: 1 }}>
            <Button size="small" disabled={historyPage <= 1} onClick={() => loadHistory(historyPage - 1, historyMode, historyQuery)} sx={{ textTransform: 'none' }}>Föregående</Button>
            <Typography sx={{ alignSelf: 'center', fontSize: '0.82rem', color: PC_COLORS.textSecondary }}>
              Sida {historyPage} av {Math.ceil(historyTotal / 20)}
            </Typography>
            <Button size="small" disabled={historyPage >= Math.ceil(historyTotal / 20)} onClick={() => loadHistory(historyPage + 1, historyMode, historyQuery)} sx={{ textTransform: 'none' }}>Nästa</Button>
          </Box>
        )}
        {docViewer && (
          <DocumentViewerDialog
            open={!!docViewer}
            onClose={() => setDocViewer(null)}
            comparisonId={docViewer.comparisonId}
            type={docViewer.type}
            fileName={docViewer.fileName}
            pdfIndex={docViewer.pdfIndex}
          />
        )}
      </Box>
    );
  }

  // ---------------------------------------------------------------------------
  // Render: Main view (upload + results)
  // ---------------------------------------------------------------------------
  const batchGroups = groupBatchFiles(batchLabelFiles, batchSupplierFiles);
  const batchLabelNoArt = batchLabelFiles.filter(f => !extractArtNrFromFilename(f.name));
  const batchSupplierNoArt = batchSupplierFiles.filter(f => !extractArtNrFromFilename(f.name));
  const resultVersion = result ? getComparisonVersion(result) : 'v1';
  const testRunVersion = testRunResults.length > 0 ? getComparisonVersion(testRunResults[0]) : 'v1';

  return (
    <Box className="product-comparison-page" sx={{ bgcolor: PC_COLORS.bg, p: { xs: 2, md: 3 }, borderRadius: '16px' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2.5, gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: { xs: '1.55rem', md: '2rem' }, fontWeight: 600, mb: 0.5, color: PC_COLORS.textPrimary, lineHeight: 1.05 }}>Produktinformationskontroll</Typography>
          <Typography sx={{ fontSize: '0.84rem', color: PC_COLORS.textSecondary, maxWidth: 760 }}>
            Jämför produktinformation mellan DAM-systemet (Dabas), etiketter (PDF) och leverantörsdokument (PDF/JPG/PNG/DOCX).
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: '0.74rem', color: PC_COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em' }}>AI-motor</Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={aiProvider}
              onChange={(_e, value) => { if (value) setAiProvider(value); }}
              sx={{
                bgcolor: PC_COLORS.surface,
                borderRadius: '999px',
                '& .MuiToggleButton-root': {
                  border: '1px solid',
                  borderColor: PC_COLORS.border,
                  color: PC_COLORS.textSecondary,
                  textTransform: 'none',
                  px: 1.8,
                  '&.Mui-selected': {
                    bgcolor: '#1F1F1B',
                    color: '#fff',
                    '&:hover': { bgcolor: '#1F1F1B' },
                  },
                },
              }}
            >
              <ToggleButton value="claude">Claude</ToggleButton>
              <ToggleButton value="openai">GPT 5.4</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          <Button
            variant="outlined"
            startIcon={<HistoryIcon />}
            onClick={() => { setHistoryError(null); setShowHistory(true); loadHistory(1, historyMode); }}
            sx={{ borderColor: PC_COLORS.borderStrong, color: PC_COLORS.textPrimary, bgcolor: PC_COLORS.surface, textTransform: 'none' }}
          >
            Historik
          </Button>
        </Stack>
      </Box>

      {/* Tabs */}
      <Box sx={{ mb: 3, p: 0.75, borderRadius: '999px', bgcolor: PC_COLORS.surface, border: '1px solid', borderColor: PC_COLORS.border, position: 'relative' }}>
        <Tabs
          value={activeTab}
          onChange={(_e, v) => setActiveTab(v)}
          sx={{
            minHeight: 0,
            '& .MuiTabs-indicator': { display: 'none' },
            '& .MuiTab-root': {
              minHeight: 0,
              minWidth: 0,
              textTransform: 'none',
              borderRadius: '999px',
              px: 2.5,
              py: 1.1,
              color: PC_COLORS.textSecondary,
              fontWeight: 500,
              '&.Mui-selected': {
                bgcolor: '#1F1F1B',
                color: '#fff',
              },
            },
          }}
        >
          <Tab label="Singelprodukt" />
          <Tab label="Batch — flera produkter" />
        </Tabs>
      </Box>

      {/* ================================================================
          TAB 0: Single product (existing flow, untouched)
          ================================================================ */}
      {activeTab === 0 && <>

      {/* Upload section */}
      {!result && testRunResults.length === 0 && (
        <ComparisonSurface>
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, mb: 2, color: PC_COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em' }}>1. Välj produkt</Typography>
          <Autocomplete
            freeSolo
            options={searchOptions}
            getOptionLabel={(opt) => typeof opt === 'string' ? opt : `${opt.artikelnummer} — ${opt.title}`}
            inputValue={artikelnummer}
            onInputChange={(_e, val, reason) => {
              if (reason === 'reset') {
                // User selected an option — extract just the artikelnummer
                const clean = val.includes(' — ') ? val.split(' — ')[0].trim() : val;
                setArtikelnummer(clean);
              } else {
                handleSearchInput(val);
              }
            }}
            onChange={(_e, val) => {
              if (val && typeof val !== 'string') setArtikelnummer(val.artikelnummer);
            }}
            loading={searchLoading}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Artikelnummer"
                placeholder="Skriv artikelnummer för att söka..."
                size="small"
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    bgcolor: PC_COLORS.surface,
                    '& fieldset': { borderColor: PC_COLORS.borderStrong },
                  },
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {searchLoading ? <CircularProgress size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            sx={{ mb: 3 }}
          />

          <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, mb: 2, color: PC_COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em' }}>2. Ladda upp filer</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 3 }}>
            <MultiDropZone
              label="Dra och släpp etikettfiler (PDF/JPG/PNG) här, eller klicka för att välja"
              accept=".pdf,.jpg,.jpeg,.png"
              files={pdfFiles}
              onFiles={setPdfFiles}
              icon={<PictureAsPdfIcon sx={{ fontSize: 40, color: 'grey.500' }} />}
              hint="Stöd för flera filer (t.ex. framsida + baksida)"
            />
            <DropZone
              label="Dra och släpp leverantörsdokument (PDF/JPG/PNG/DOCX)"
              accept=".pdf,.jpg,.jpeg,.png,.docx"
              file={supplierFile}
              onFile={handleSupplierFileChange}
              icon={<DescriptionIcon sx={{ fontSize: 40, color: 'grey.500' }} />}
            />
          </Box>

          {pdfFiles.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={<Switch checked={showSinglePreviews} onChange={(_e, checked) => setShowSinglePreviews(checked)} />}
                label="Visa etikettfiler och beskärning"
              />
              {showSinglePreviews && (
                <Box sx={{ mt: 1 }}>
                  <Alert severity="info" sx={{ mb: 1.5, borderColor: '#BFDBFE', bgcolor: '#EFF6FF' }} variant="outlined">
                    Originalfilen visas i previewn. När beskärning finns markeras området i grönt här, medan AI får den beskurna versionen från popupen.
                  </Alert>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
                    {pdfFiles.map((file) => {
                      const key = fileStableKey(file);
                      return (
                        <LabelPreviewCard
                          key={key}
                          file={file}
                          cropBottomPct={singleCropBottomPctByFile[key] ?? 0}
                          onCropBottomPctChange={(v) => setSingleCropBottomPctByFile(prev => ({ ...prev, [key]: v }))}
                          pdfCropSelection={singlePdfCropSelectionByFile[key] ?? null}
                          onOpenPdfCrop={isPdfFile(file) ? () => setSinglePdfCropDialogKey(key) : undefined}
                        />
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {error && <Alert severity="error" sx={{ mb: 2, borderColor: PC_COLORS.critBorder, bgcolor: PC_COLORS.critBg }} variant="outlined">{error}</Alert>}

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {SHOW_LEGACY_V1_ACTIONS && (
              <>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CompareArrowsIcon />}
                  onClick={handleCompare}
                  disabled={loading || !artikelnummer.trim() || (pdfFiles.length === 0 && !supplierFile)}
                  sx={{ borderRadius: '999px', px: 3, textTransform: 'none', bgcolor: '#1F1F1B', '&:hover': { bgcolor: '#1F1F1B' } }}
                >
                  {loading ? 'Analyserar...' : 'Jämför produktinformation'}
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={handleTestRun}
                  disabled={loading || !artikelnummer.trim() || (pdfFiles.length === 0 && !supplierFile)}
                  sx={{ borderRadius: '999px', px: 3, textTransform: 'none', borderColor: PC_COLORS.borderStrong, color: PC_COLORS.textPrimary, bgcolor: PC_COLORS.surface }}
                >
                  {loading && testProgress ? `Testkörning ${testProgress.current}/${testProgress.total}` : 'Testkör x3'}
                </Button>
              </>
            )}
            <Button
              variant="contained"
              size="large"
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CompareArrowsIcon />}
              onClick={handleCompareV2}
              disabled={loading || !artikelnummer.trim() || (pdfFiles.length === 0 && !supplierFile)}
              sx={{ borderRadius: '999px', px: 3, textTransform: 'none', bgcolor: '#2563EB', '&:hover': { bgcolor: '#1D4ED8' } }}
            >
              {loading ? 'Analyserar...' : 'Jämför produktinformation 2'}
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={handleTestRunV2}
              disabled={loading || !artikelnummer.trim() || (pdfFiles.length === 0 && !supplierFile)}
              sx={{ borderRadius: '999px', px: 3, textTransform: 'none', borderColor: '#93C5FD', color: '#1D4ED8', bgcolor: '#EFF6FF' }}
            >
              {loading && testProgress ? `Testkörning ${testProgress.current}/${testProgress.total}` : 'Testkör x3 v2'}
            </Button>
          </Box>

          {loading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress />
              <Typography sx={{ mt: 1, fontSize: '0.82rem', color: PC_COLORS.textSecondary }}>
                {testProgress
                  ? `Kör samma input ${testProgress.total} gånger för att mäta variationer. Pågående körning: ${testProgress.current} av ${testProgress.total}.`
                  : 'Extraherar text med AI och jämför fält — detta kan ta 15–60 sekunder beroende på dokumentstorlek...'}
              </Typography>
            </Box>
          )}
        </ComparisonSurface>
      )}

      {/* Results section */}
      {result && (
        <Box>
          <Box sx={{ mb: 1.5 }}>
            <Typography sx={{ fontSize: { xs: '1.55rem', md: '2rem' }, fontWeight: 600, color: PC_COLORS.textPrimary, lineHeight: 1.05 }}>
              {result.produktnamn}
            </Typography>
          </Box>
          <Box sx={STICKY_HEADER_SX}>
            <Box sx={{ pt: 0.25 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2.5, gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ mt: 0.5, fontSize: '0.82rem', color: PC_COLORS.textSecondary }}>
                    Artikelnummer: {result.artikelnummer}
                    {result.assetId ? ' • Hittad i DAM' : ' • Ej hittad i DAM'}
                    {` • AI: ${formatAiProvider(result.aiProvider, result.aiModel)}`}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                  <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={handleReset} sx={{ borderColor: PC_COLORS.borderStrong, color: PC_COLORS.textPrimary, bgcolor: PC_COLORS.surface, textTransform: 'none' }}>
                    Ny jämförelse
                  </Button>
                </Stack>
              </Box>

              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <ComparisonVersionBadge version={resultVersion} />
                <OverallStatusBadge status={result.overallStatus} />
                <Chip label={`AI: ${formatAiProvider(result.aiProvider, result.aiModel)}`} size="small" variant="outlined" sx={{ borderColor: PC_COLORS.borderStrong, bgcolor: PC_COLORS.surface }} />
                {getCroppedPdfChipLabel(result) && (
                  <Tooltip title={getCroppedLabelAiInputs(result).map((input) => input.aiFileName || 'Beskuren PDF').join(', ')}>
                    <Chip
                      label={getCroppedPdfChipLabel(result)}
                      size="small"
                      color="success"
                      icon={<CropFreeIcon />}
                      sx={{ bgcolor: PC_COLORS.okBg, borderColor: PC_COLORS.okBorder }}
                    />
                  </Tooltip>
                )}
                {getPdfDocumentEntries(result).map((entry) => (
                  <Chip
                    key={`${result._id || 'result'}-pdf-${entry.pdfIndex ?? 'legacy'}-${entry.fileName}`}
                    label={`PDF: ${entry.fileName}`}
                    size="small"
                    variant="outlined"
                    icon={<PictureAsPdfIcon />}
                    onClick={() => handleOpenResultDocument(result._id, 'pdf', entry.fileName, entry.pdfIndex)}
                    sx={{ borderColor: PC_COLORS.borderStrong, bgcolor: PC_COLORS.surface, cursor: 'pointer' }}
                  />
                ))}
                {result.wordFileName && (
                  <Chip
                    label={`Leverantör: ${result.wordFileName}`}
                    size="small"
                    variant="outlined"
                    icon={<DescriptionIcon />}
                    onClick={() => handleOpenResultDocument(result._id, 'word', result.wordFileName)}
                    sx={{ borderColor: PC_COLORS.borderStrong, bgcolor: PC_COLORS.surface, cursor: 'pointer' }}
                  />
                )}
              </Box>

              <CriticalWarningBanner warnings={result.regulatoryWarnings} />
              {isV2Comparison(result) && (
                <Alert severity="info" sx={{ mb: 2, borderColor: '#BFDBFE', bgcolor: '#EFF6FF' }} variant="outlined">
                  Källa in: etikett = {formatV2SourceMode(result.v2Result.sourceAudit.labelMode)}, leverantör = {formatV2SourceMode(result.v2Result.sourceAudit.supplierMode)}.
                </Alert>
              )}
              {isV2Comparison(result)
                ? <V2ScoreBar v2Result={result.v2Result} />
                : <ComparisonScoreBar fields={result.fields} />}
            </Box>
          </Box>

          {isV2Comparison(result)
            ? <V2ComparisonView v2Result={result.v2Result} showSourceAuditAlert={false} />
            : <ComparisonTable fields={result.fields} />}

          <RawTextSection rawLabelText={result.rawLabelText} rawSupplierText={result.rawSupplierText} />
        </Box>
      )}

      {testRunResults.length > 0 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2.5, gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: { xs: '1.55rem', md: '2rem' }, fontWeight: 600, color: PC_COLORS.textPrimary, lineHeight: 1.05 }}>
                {testRunVersion === 'v2' ? 'Testkörning x3 v2' : 'Testkörning x3'}
              </Typography>
              <Typography sx={{ mt: 0.5, fontSize: '0.82rem', color: PC_COLORS.textSecondary }}>
                Artikelnummer: {testRunResults[0]?.artikelnummer} • AI: {formatAiProvider(testRunResults[0]?.aiProvider, testRunResults[0]?.aiModel)}
                {testRunGroupId ? ` • Grupp: ${testRunGroupId}` : ''}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                <ComparisonVersionBadge version={testRunVersion} />
                <OverallStatusBadge status={testRunResults[0]?.overallStatus || 'ok'} />
              </Box>
            </Box>
            <Stack direction="row" spacing={1}>
              <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={handleReset} sx={{ borderColor: PC_COLORS.borderStrong, color: PC_COLORS.textPrimary, bgcolor: PC_COLORS.surface, textTransform: 'none' }}>
                Ny körning
              </Button>
            </Stack>
          </Box>

          <Alert severity="info" sx={{ mb: 2, borderColor: '#BFDBFE', bgcolor: '#EFF6FF' }} variant="outlined">
            Tre identiska körningar har sparats som testresultat. Öppna <strong>Historik</strong> och slå på <strong>Visa testresultat</strong> för att se dem senare.
          </Alert>

          <TextField
            label="Sammanfattning att bifoga"
            value={testRunSummary}
            multiline
            minRows={10}
            fullWidth
            InputProps={{ readOnly: true }}
            sx={{ mb: 2.5, '& .MuiOutlinedInput-root': { bgcolor: PC_COLORS.surface, borderRadius: '12px' } }}
          />

          <Typography sx={{ fontSize: '0.92rem', fontWeight: 600, color: PC_COLORS.textPrimary, mb: 1.25 }}>
            Resultat per körning
          </Typography>
          {testRunResults.map((run, index) => (
            isV2Comparison(run)
              ? <V2RunResultCard key={`${run.testRunGroupId || 'test'}-${index}`} result={run} index={index} />
              : <BatchResultCard key={`${run.testRunGroupId || 'test'}-${index}`} result={run} index={index} />
          ))}
        </Box>
      )}

      </> /* end TAB 0 */}

      {/* ================================================================
          TAB 1: Batch — flera produkter
          ================================================================ */}
      {activeTab === 1 && (
        <Box>
          {/* Upload area — shown until batch run starts */}
          {batchResults.length === 0 && (
            <ComparisonSurface>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, mb: 0.5, color: PC_COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Ladda upp filer för flera produkter
              </Typography>
              <Typography sx={{ fontSize: '0.84rem', color: PC_COLORS.textSecondary, mb: 2 }}>
                Artikelnummer hämtas automatiskt från filnamnet (filnamnet ska börja med siffror, t.ex. <strong>97034</strong>_framsida.pdf).
              </Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 3 }}>
                <MultiDropZone
                  label="Dra och släpp etikettfiler (PDF/JPG/PNG) för alla produkter"
                  accept=".pdf,.jpg,.jpeg,.png"
                  files={batchLabelFiles}
                  onFiles={setBatchLabelFiles}
                  icon={<PictureAsPdfIcon sx={{ fontSize: 40, color: 'grey.500' }} />}
                  hint="Filnamnen ska börja med artikelnummer, t.ex. 97034_framsida.png"
                />
                <MultiDropZone
                  label="Dra och släpp leverantörsdokument (PDF/JPG/PNG/DOCX) för alla produkter"
                  accept=".pdf,.jpg,.jpeg,.png,.docx"
                  files={batchSupplierFiles}
                  onFiles={handleBatchSupplierFilesChange}
                  icon={<DescriptionIcon sx={{ fontSize: 40, color: 'grey.500' }} />}
                  hint="Filnamnen ska börja med artikelnummer, t.ex. 97034_spec.docx. Gamla .doc-filer måste först sparas om till .docx."
                />
              </Box>

              {batchLabelFiles.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <FormControlLabel
                    control={<Switch checked={showBatchPreviews} onChange={(_e, checked) => setShowBatchPreviews(checked)} />}
                    label="Visa previews för justering"
                  />
                  {showBatchPreviews && (
                    <Box sx={{ mt: 1 }}>
                      <Alert severity="info" sx={{ mb: 1.5, borderColor: '#BFDBFE', bgcolor: '#EFF6FF' }} variant="outlined">
                        Justera beskärning per etikettfil. Detta beskär nedre delen innan AI-analys.
                      </Alert>
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
                        {batchLabelFiles.map((file) => {
                          const key = fileStableKey(file);
                          return (
                            <LabelPreviewCard
                              key={key}
                              file={file}
                              cropBottomPct={batchCropBottomPctByFile[key] ?? 0}
                              onCropBottomPctChange={(v) => setBatchCropBottomPctByFile(prev => ({ ...prev, [key]: v }))}
                            />
                          );
                        })}
                      </Box>
                    </Box>
                  )}
                </Box>
              )}

              {/* Warn about files without article number */}
              {(batchLabelNoArt.length > 0 || batchSupplierNoArt.length > 0) && (
                <Alert severity="warning" sx={{ mb: 2, borderColor: '#F9C06A', bgcolor: '#FEF3E2' }} variant="outlined">
                  Dessa filer saknar artikelnummer i filnamnet och ignoreras:{' '}
                  {[...batchLabelNoArt, ...batchSupplierNoArt].map(f => f.name).join(', ')}
                </Alert>
              )}

              {/* Preview table of detected groups */}
              {batchGroups.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography sx={{ mb: 1, fontSize: '0.82rem', fontWeight: 600, color: PC_COLORS.textPrimary }}>
                    {batchGroups.length} produkt(er) identifierade:
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ borderColor: PC_COLORS.border, borderRadius: '10px' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: PC_COLORS.surface2 }}>
                          <TableCell sx={{ fontWeight: 600, color: PC_COLORS.textSecondary }}>Artikelnummer</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: PC_COLORS.textSecondary }}>Etikettfiler</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: PC_COLORS.textSecondary }}>Leverantörsdokument</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {batchGroups.map(g => (
                          <TableRow key={g.artikelnummer}>
                            <TableCell>{g.artikelnummer}</TableCell>
                            <TableCell>
                              {g.labelFiles.length > 0
                                ? g.labelFiles.map(f => f.name).join(', ')
                                : <Typography component="span" variant="caption" color="text.disabled">Ingen</Typography>
                              }
                            </TableCell>
                            <TableCell>
                              {g.supplierFile
                                ? g.supplierFile.name
                                : <Typography component="span" variant="caption" color="text.disabled">Ingen</Typography>
                              }
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {batchError && <Alert severity="error" sx={{ mb: 2, borderColor: PC_COLORS.critBorder, bgcolor: PC_COLORS.critBg }} variant="outlined">{batchError}</Alert>}

              <Button
                variant="contained"
                size="large"
                startIcon={batchLoading ? <CircularProgress size={20} color="inherit" /> : <CompareArrowsIcon />}
                onClick={handleBatchRun}
                disabled={batchLoading || batchGroups.length === 0}
                sx={{ borderRadius: '999px', px: 3, textTransform: 'none', bgcolor: '#1F1F1B', '&:hover': { bgcolor: '#1F1F1B' } }}
              >
                {batchLoading
                  ? `Analyserar ${batchProgress?.art} (${batchProgress?.current}/${batchProgress?.total})...`
                  : `Kör jämförelse för ${batchGroups.length} produkt${batchGroups.length !== 1 ? 'er' : ''}`
                }
              </Button>
            </ComparisonSurface>
          )}

          {/* Progress bar during batch run */}
          {batchLoading && batchProgress && (
            <Box sx={{ mb: 2 }}>
              <LinearProgress
                variant="determinate"
                value={(batchProgress.current / batchProgress.total) * 100}
                sx={{ height: 8, borderRadius: '999px', bgcolor: '#E8E6E1', '& .MuiLinearProgress-bar': { bgcolor: PC_COLORS.accent } }}
              />
              <Typography sx={{ mt: 1, fontSize: '0.82rem', color: PC_COLORS.textSecondary }}>
                Bearbetar artikelnummer {batchProgress.art} ({batchProgress.current} av {batchProgress.total})...
              </Typography>
            </Box>
          )}

          {/* Batch results */}
          {batchResults.length > 0 && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: PC_COLORS.textPrimary }}>
                  Resultat — {batchResults.length} av {batchProgress ? batchProgress.total : batchResults.length} produkter klara
                </Typography>
                {!batchLoading && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={() => {
                      setBatchResults([]);
                      setBatchLabelFiles([]);
                      setShowBatchPreviews(false);
                      setBatchCropBottomPctByFile({});
                      setBatchSupplierFiles([]);
                      setBatchError(null);
                    }}
                    sx={{ borderColor: PC_COLORS.borderStrong, color: PC_COLORS.textPrimary, bgcolor: PC_COLORS.surface, textTransform: 'none' }}
                  >
                    Ny batch
                  </Button>
                )}
              </Box>

              {/* Summary chips */}
              {!batchLoading && (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 1.5, mb: 3 }}>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderColor: PC_COLORS.border, borderRadius: '10px' }}>
                    <Typography className="pc-mono" sx={{ fontSize: '1.8rem', color: PC_COLORS.textPrimary }}>{batchResults.length}</Typography>
                    <Typography sx={{ fontSize: '0.74rem', color: PC_COLORS.textSecondary }}>Totalt</Typography>
                  </Paper>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderColor: PC_COLORS.okBorder, borderRadius: '10px', bgcolor: PC_COLORS.okBg }}>
                    <Typography className="pc-mono" sx={{ fontSize: '1.8rem', color: PC_COLORS.ok }}>
                      {batchResults.filter(r => r.overallStatus === 'ok').length}
                    </Typography>
                    <Typography sx={{ fontSize: '0.74rem', color: PC_COLORS.ok }}>OK</Typography>
                  </Paper>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderColor: PC_COLORS.warnBorder, borderRadius: '10px', bgcolor: PC_COLORS.warnBg }}>
                    <Typography className="pc-mono" sx={{ fontSize: '1.8rem', color: '#92400E' }}>
                      {batchResults.filter(r => r.overallStatus === 'has_mismatches').length}
                    </Typography>
                    <Typography sx={{ fontSize: '0.74rem', color: '#92400E' }}>Avvikelser</Typography>
                  </Paper>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderColor: PC_COLORS.critBorder, borderRadius: '10px', bgcolor: PC_COLORS.critBg }}>
                    <Typography className="pc-mono" sx={{ fontSize: '1.8rem', color: PC_COLORS.crit }}>
                      {batchResults.filter(r => r.overallStatus === 'has_regulatory_warnings').length}
                    </Typography>
                    <Typography sx={{ fontSize: '0.74rem', color: PC_COLORS.crit }}>Varningar</Typography>
                  </Paper>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderColor: PC_COLORS.border, borderRadius: '10px' }}>
                    <Typography className="pc-mono" sx={{ fontSize: '1.8rem', color: PC_COLORS.textSecondary }}>
                      {batchResults.filter(r => !r.assetId).length}
                    </Typography>
                    <Typography sx={{ fontSize: '0.74rem', color: PC_COLORS.textSecondary }}>Ej i DAM</Typography>
                  </Paper>
                </Box>
              )}

              {/* One card per product */}
              {batchResults.map((r, idx) => (
                <BatchResultCard key={`${r.artikelnummer}-${idx}`} result={r} index={idx} />
              ))}
            </Box>
          )}
        </Box>
      )}

      <LabelPdfCropDialog
        open={!!activeSinglePdfCropFile}
        file={activeSinglePdfCropFile}
        initialSelection={singlePdfCropDialogKey ? singlePdfCropSelectionByFile[singlePdfCropDialogKey] ?? null : null}
        onClose={() => setSinglePdfCropDialogKey(null)}
        onSave={(selection) => {
          if (!singlePdfCropDialogKey) {
            return;
          }
          setSinglePdfCropSelectionByFile((prev) => ({
            ...prev,
            [singlePdfCropDialogKey]: selection,
          }));
          setSinglePdfCropDialogKey(null);
        }}
      />

      {docViewer && (
        <DocumentViewerDialog
          open={!!docViewer}
          onClose={() => setDocViewer(null)}
          comparisonId={docViewer.comparisonId}
          type={docViewer.type}
          fileName={docViewer.fileName}
          pdfIndex={docViewer.pdfIndex}
        />
      )}
    </Box>
  );
};

export default ProductComparisonPage;
